import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  CheckCircleOutline as ApproveIcon,
  Link as LinkIcon,
  DeleteOutline as DeleteIcon,
  Download as DownloadIcon,
  History as HistoryIcon,
  LocalShipping as DeliverIcon,
  OpenInNew as OpenInNewIcon,
  PrecisionManufacturing as ProductionIcon,
  Print as PrintIcon,
  QrCode2 as QrCodeIcon,
  Refresh as RefreshIcon,
  Undo as UndoIcon,
  VisibilityOutlined as ViewIcon,
} from '@mui/icons-material';
import axios from '../utils/axios';
import PageHeader from '../components/ui/PageHeader';
import DateRangeField from '../components/ui/DateRangeField';
import PickingJobsPanel from '../components/fulfillment/PickingJobsPanel';
import ConfirmDialog from '../components/fulfillment/ConfirmDialog';
import PalletsPanel from '../components/fulfillment/PalletsPanel';
import LoadingSessionsPanel from '../components/fulfillment/LoadingSessionsPanel';
import FulfillmentProductThumb from '../components/fulfillment/FulfillmentProductThumb';
import B2bChip from '../components/fulfillment/B2bChip';
import WbSupplyShippingFields, {
  normalizeShippingForm,
  shippingConfirmBody,
  shippingFormReady,
} from '../components/fulfillment/WbSupplyShippingFields.jsx';
import ProductFilters, { buildFilterParams } from '../components/ui/ProductFilters';
import usePrinter from '../hooks/usePrinter';
import { useNotify } from '../components/NotifyProvider';
import { newPrintIdempotencyKey, printClient } from '../utils/printClient';
import {
  orderSelectedSourceIds,
  taskCanPrintLabel,
  taskSourceId,
} from '../utils/printerState.mjs';
import {
  LABELING_MODES,
  normalizeFulfillmentMp,
  normalizeLabelingMode,
  pickFulfillmentAccount,
  yandexShipmentActPath,
  yandexShipmentClosePath,
} from '../utils/fulfillmentApi.mjs';
import {
  aggregatePickingListByArticle,
  defaultTaskSort,
  displayArticle,
  fulfillmentTabKey,
  resolveVisibleTabIndex,
  formatBulkNotice,
  formatOrderAge,
  humanizeError,
  B2B_MIXED_SELECTION_HINT,
  isB2bTask,
  selectionB2bType,
  supplyB2bConflict,
  supplyB2bType,
  marketplaceChipLabel,
  normalizedStatus,
  openOrDownloadPdfBlob,
  orderAgeTone,
  pickingListFilename,
  productIdentifiersLine,
  statusChipSx,
  summarizeBulkResult,
  supplyStatusChipSx,
  taskExternalId,
  taskMarketplace,
  techCardLabel,
  warehousePresentation,
} from './fulfillmentUi.mjs';

const PRINTER_NOT_SELECTED_MSG = 'Выберите принтер в Настройки → Печать';

const PAGE_SIZE_KEY = 'fulfillment_rows_per_page';
const PICKING_LABELING_MODE_KEY = 'fulfillment_picking_labeling_mode';

function readPickingLabelingMode() {
  try {
    return normalizeLabelingMode(localStorage.getItem(PICKING_LABELING_MODE_KEY));
  } catch {
    return LABELING_MODES.AFTER_TRANSFER;
  }
}
const ALL_TABS = [
  { key: 'tasks:new', label: 'Новые', kind: 'tasks', statusGroup: 'new' },
  { key: 'tasks:waiting_assembly', label: 'Ожидают сборки', kind: 'tasks', statusGroup: 'waiting_assembly' },
  { key: 'tasks:assembly', label: 'Сборка', kind: 'tasks', statusGroup: 'assembly' },
  { key: 'pickingJobs:', label: 'Задания', kind: 'pickingJobs' },
  { key: 'pallets:', label: 'Палеты', kind: 'pallets' },
  { key: 'loading:', label: 'Погрузка', kind: 'loading' },
  { key: 'tasks:collected', label: 'Собрано', kind: 'tasks', statusGroup: 'collected' },
  { key: 'supplies:', label: 'Поставки', kind: 'supplies' },
  { key: 'reshipment:active', label: 'Перепоставка', kind: 'reshipment', statusGroup: 'active', wbOnly: true },
  { key: 'tasks:delivery', label: 'В доставке', kind: 'tasks', statusGroup: 'delivery' },
  { key: 'tasks:sold', label: 'Выкуплен', kind: 'tasks', statusGroup: 'sold' },
  { key: 'tasks:return', label: 'Возврат', kind: 'tasks', statusGroup: 'return' },
  { key: 'tasks:canceled', label: 'Отменённые', kind: 'tasks', statusGroup: 'canceled' },
];
/** @deprecated alias — use visible tabs from marketplace */
const TABS = ALL_TABS;
const WORKFLOW_SELECTABLE_GROUPS = new Set(['new', 'waiting_assembly']);
/** Массовое скачивание PDF этикеток в АРМ: вкладки «Сборка» и «Собрано». Одиночная иконка — везде, где этикетка уже доступна. */
const PRINT_SELECTABLE_GROUPS = new Set(['assembly', 'collected']);
/** Макс. строк в таблице / mass-move Новые → Ожидают сборки (сервер clamp тот же). */
const MAX_TASKS_PAGE_SIZE = 500;
const MAX_ASSEMBLY_BATCH = 500;
/** Фоновый sync Ozon carriage при открытии «Поставки» — не чаще раза в 3 мин на кабинет. */
const OZON_CARRIAGE_SYNC_TTL_MS = 3 * 60 * 1000;
/**
 * Лимит массовой выгрузки этикеток PDF (как page size / assembly batch).
 * WB stickers API чанкуется по 100 на бэкенде; Ozon package-label — по 20.
 */
const MAX_STICKER_BATCH = 500;
/** Вкладки, где можно искать/связывать/создавать заказ в МойСклад. */
const MS_LINKABLE_GROUPS = new Set(['new', 'waiting_assembly', 'assembly']);
const MS_ORDER_HREF = (msId) => (
  msId ? `https://online.moysklad.ru/app/#customerorder/edit?id=${encodeURIComponent(msId)}` : null
);
const TASK_COLUMNS = [
  // Дата в ячейке — сортируем по created_at_mp (канонический oldest→newest на new/waiting/assembly).
  { key: 'created_at_mp', label: 'Задание', sortable: true },
  { key: 'article', label: 'Товар', sortable: true },
  { key: 'supplier_status', label: 'Статус', sortable: true },
  { key: 'warehouse_id', label: 'Склад / тип', sortable: true },
  { key: 'shipment_date', label: 'Дата отгрузки', sortable: true },
  { key: 'supply_id', label: 'Поставка', sortable: true },
  { key: 'sale_price', label: 'Цена', sortable: true, desktopOnly: true },
  { key: 'ms_id', label: 'МойСклад', desktopOnly: true },
  { key: '__actions', label: '', align: 'right' },
];
const SUPPLY_COLUMNS = [
  { key: '__expand', label: '' },
  { key: 'name', label: 'Поставка', sortable: true },
  { key: '__items', label: 'Товары' },
  { key: '__warehouse', label: 'Склад' },
  { key: 'done', label: 'Статус WB', sortable: true },
  { key: 'created_at_mp', label: 'Даты', sortable: true },
  { key: '__actions', label: '', align: 'right' },
];
const OZON_SUPPLY_COLUMNS = [
  { key: '__expand', label: '' },
  { key: 'name', label: 'Отгрузка', sortable: true },
  { key: '__items', label: 'Заказы' },
  { key: '__method', label: 'Способ доставки' },
  { key: 'done', label: 'Статус', sortable: true },
  { key: 'created_at_mp', label: 'Даты', sortable: true },
  { key: '__actions', label: '', align: 'right' },
];
function msSyncKindLabel(kind) {
  if (kind === 'ship_to_collected') {
    return {
      progress: 'Передача в сборку (маркетплейс):',
      doneOk: 'Заказы переданы в сборку',
      doneFail: 'Не удалось передать заказы в сборку',
      donePartial: (failed, total) => `Передано в сборку с ошибками: ${failed} из ${total}`,
    };
  }
  if (kind === 'labels_pdf') {
    return {
      progress: 'Получение этикеток Яндекса:',
      doneOk: 'PDF этикеток готов',
      doneFail: 'Не удалось получить этикетки Яндекса',
      donePartial: (failed, total) => `Этикетки получены с ошибками: ${failed} из ${total}`,
    };
  }
  if (kind === 'create_production') {
    return {
      progress: 'Создание производства в МойСклад:',
      doneOk: 'Производство создано',
      doneFail: 'Не удалось создать производство',
      donePartial: (failed, total) => `Производство создано с ошибками: ${failed} из ${total}`,
    };
  }
  return {
    progress: 'Обновление статусов в МойСклад:',
    doneOk: 'Статусы в МойСклад обновлены',
    doneFail: 'Не удалось обновить статусы в МойСклад',
    donePartial: (failed, total) => `Статусы в МойСклад обновлены с ошибками: ${failed} из ${total}`,
  };
}

function initialPageSize() {
  const saved = Number(localStorage.getItem(PAGE_SIZE_KEY));
  return [25, 50, 100, 200, MAX_TASKS_PAGE_SIZE].includes(saved) ? saved : 50;
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
}

/** YYYY-MM-DD → дд.мм.гггг без сдвига таймзоны. */
function formatDateOnly(value) {
  const text = String(value || '').trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return text || '—';
  return `${match[3]}.${match[2]}.${match[1]}`;
}

function money(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return `${(number / 100).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`;
}

function apiError(error) {
  const data = error?.response?.data || {};
  return humanizeError(data, error?.message || 'Не удалось выполнить операцию');
}

function taskStatusTooltip(row, marketplace) {
  const errorText = String(row?.shipment_error || row?.last_error || '').trim();
  if (normalizedStatus(row).code === 'shipment_error' && errorText) return errorText;
  if (marketplace === 'ozon') {
    return `packer: ${row?.packer_status || '—'}; ozon: ${row?.ozon_status || '—'}`;
  }
  if (marketplace === 'yandex') {
    return `packer: ${row?.packer_status || '—'}; ya: ${row?.ya_status || '—'}${row?.ya_substatus ? ` / ${row.ya_substatus}` : ''}`;
  }
  return `supplierStatus: ${row?.supplier_status || '—'}; wbStatus: ${row?.wb_status || '—'}`;
}

function noticeAlertSeverity(text) {
  const value = String(text || '');
  if (value.includes('Пропущено:') && /Передано: 0\b/.test(value)) return 'error';
  if (value.includes('Пропущено:')) return 'warning';
  return 'success';
}

function idempotencyKey(prefix) {
  const random = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  return `${prefix}-${random}`;
}

function ageChipSx(tone) {
  return () => {
    if (tone === 'critical') {
      return {
        bgcolor: '#FDE5E8',
        color: '#C83E52',
      };
    }
    if (tone === 'error') {
      return {
        bgcolor: '#FFE6DF',
        color: '#E2583E',
      };
    }
    if (tone === 'warning') {
      return {
        bgcolor: '#FFF0D8',
        color: '#FF7658',
      };
    }
    if (tone === 'success') {
      return {
        bgcolor: '#E1F5E7',
        color: '#209657',
      };
    }
    return { bgcolor: '#F0EFF4', color: '#777486' };
  };
}

const cargoChipSx = {
  bgcolor: '#F0EFF4',
  color: '#777486',
  fontWeight: 700,
};

function moscowTodayYmd() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow' }).format(new Date());
}

/** Колонка «Товар»: для Ozon/Yandex multi-SKU — стек строк; фото из cards, не WB CDN. */
function TaskProductCell({ row }) {
  const marketplace = taskMarketplace(row);
  const products = Array.isArray(row.products) ? row.products.filter(Boolean) : [];
  const useOzonLines = (marketplace === 'ozon' || marketplace === 'yandex') && products.length > 0;

  if (useOzonLines) {
    return (
      <Stack spacing={1}>
        {products.map((item, index) => {
          const article = item.article || item.offer_id || 'Артикул не указан';
          const qty = Number(item.quantity) || 0;
          const key = `${item.sku || article}-${index}`;
          return (
            <Stack key={key} direction="row" spacing={1} alignItems="flex-start">
              <FulfillmentProductThumb
                task={row}
                product={item}
                imageUrl={item.image_url}
                marketplace={marketplace}
                size={52}
                sx={{ width: { xs: 44, sm: 52 }, height: { xs: 58, sm: 68 }, objectFit: 'cover', borderRadius: 1.5 }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="bodyMedium" fontWeight={700} sx={{ whiteSpace: 'normal' }}>
                  {`${qty} шт, ${article}`}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="p">
                  {productIdentifiersLine(row, {
                    marketplace,
                    sku: item.sku,
                    barcode: item.barcode || (products.length === 1 ? row.barcode : null),
                  })}
                </Typography>
                {item.name ? (
                  <Typography variant="caption" color="text.secondary" component="p">
                    {item.name}
                  </Typography>
                ) : null}
              </Box>
            </Stack>
          );
        })}
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <FulfillmentProductThumb
        task={row}
        marketplace={marketplace}
        size={52}
        sx={{ width: { xs: 44, sm: 52 }, height: { xs: 58, sm: 68 }, objectFit: 'cover', borderRadius: 1.5 }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="bodyMedium" fontWeight={700} sx={{ whiteSpace: 'normal' }}>
          {displayArticle(row)}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="p">
          {productIdentifiersLine(row, { marketplace })}
        </Typography>
        {(row.title || row.product?.name || row.product?.podgruppa) && (
          <Typography variant="caption" color="text.secondary" component="p">
            {row.title || row.product?.name || row.product?.podgruppa}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

export default function Fulfillment() {
  const user = useSelector((state) => state.auth.user);
  const canExecute = user?.role === 'admin' || user?.permissions?.fulfillmentExecute === true;
  const canUsePrintQueue = user?.role === 'admin' || user?.permissions?.printQueue === true;
  /** Скачивание PDF этикеток заказов в АРМ (без принтера). */
  const canDownloadLabels = canUsePrintQueue || canExecute;
  const printer = usePrinter({ enabled: canUsePrintQueue });
  const { success: notifySuccess, error: notifyError, warning: notifyWarning } = useNotify();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkRef = useRef({
    account_id: searchParams.get('account_id') || '',
    mp: searchParams.get('mp') || '',
    applied: false,
  });
  const [tabKey, setTabKey] = useState(() => fulfillmentTabKey(ALL_TABS[0]));
  const [confirm, setConfirm] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const selectedAccount = accounts.find((item) => String(item.id) === String(accountId)) || null;
  const accountMarketplace = normalizeFulfillmentMp(selectedAccount?.marketplace) || 'wb';
  const isOzonAccount = accountMarketplace === 'ozon';
  const isWbAccount = accountMarketplace === 'wb';
  const isYandexAccount = accountMarketplace === 'yandex';
  const isCarriageAccount = isOzonAccount || isYandexAccount;
  const mpName = isYandexAccount ? 'Yandex' : (isOzonAccount ? 'Ozon' : 'WB');
  const filtersMp = isOzonAccount ? '2' : (isYandexAccount ? '3' : '1');
  const tabs = useMemo(
    () => ALL_TABS.filter((item) => {
      if (item.wbOnly && !isWbAccount) return false;
      return true;
    }),
    [isWbAccount],
  );
  const [warehouseNames, setWarehouseNames] = useState({});
  const [tabCounts, setTabCounts] = useState({
    new: null,
    waiting_assembly: null,
    assembly: null,
    collected: null,
  });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [ozonPendingMethods, setOzonPendingMethods] = useState([]);
  const [ozonCreateMethodId, setOzonCreateMethodId] = useState('');
  const [filters, setFilters] = useState({ mp: '1' });
  const [query, setQuery] = useState('');
  const [supplyId, setSupplyId] = useState('');
  /** WB: фильтр МГТ/СГТ (cargo_type). Пустая строка = все. */
  const [cargoType, setCargoType] = useState('');
  /** Фильтр склада (warehouse_id) для WB, Ozon и Yandex. Пустая строка = все. */
  const [warehouseId, setWarehouseId] = useState('');
  /** Yandex-only: диапазон даты отгрузки YYYY-MM-DD, по умолчанию сегодня (МСК). */
  const [shipmentDateFrom, setShipmentDateFrom] = useState(() => moscowTodayYmd());
  const [shipmentDateTo, setShipmentDateTo] = useState(() => moscowTodayYmd());
  const [ozonWarehouses, setOzonWarehouses] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);
  const [sort, setSort] = useState(() => defaultTaskSort(ALL_TABS[0].statusGroup));
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [writeState, setWriteState] = useState(null);
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [openSupplies, setOpenSupplies] = useState([]);
  const [targetSupplyId, setTargetSupplyId] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [ozonCreateOpen, setOzonCreateOpen] = useState(false);
  const [inlineSupplyCreate, setInlineSupplyCreate] = useState(false);
  const [supplyName, setSupplyName] = useState('');
  const [deliverTarget, setDeliverTarget] = useState(null);
  const [shippingForm, setShippingForm] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingError, setShippingError] = useState('');
  const [pickingCreateOpen, setPickingCreateOpen] = useState(false);
  const [pickingName, setPickingName] = useState('');
  const [pickingAssignee, setPickingAssignee] = useState('');
  const [pickingAssignees, setPickingAssignees] = useState([]);
  const [pickingLabelingMode, setPickingLabelingMode] = useState(readPickingLabelingMode);
  const [productionDialog, setProductionDialog] = useState({
    open: false,
    items: [],
    selections: {},
    orderIds: [],
    restCount: 0,
  });
  const [pickingRefreshKey, setPickingRefreshKey] = useState(0);
  /** Фоновый MS sync / ship_to_collected: { job_id, kind, total, done, failed, status }. */
  const [msSync, setMsSync] = useState(null);
  const msSyncPollRef = useRef(null);
  const msSyncHideTimerRef = useRef(null);
  const loadRowsRef = useRef(null);
  const loadTabCountsRef = useRef(null);
  const [journal, setJournal] = useState({ open: false, task: null, events: [], loading: false });
  const [printAction, setPrintAction] = useState('');
  const [pickingListPrinting, setPickingListPrinting] = useState(false);
  const [minuteNow, setMinuteNow] = useState(Date.now());
  const operationKeys = useRef(new Map());
  const selectedTaskRows = useRef(new Map());
  /** Поколение loadRows — чтобы фоновый carriage-sync не затирал уже другой вкладки/кабинета. */
  const rowsLoadGenRef = useRef(0);
  /** Ключ списка account+mp+kind — при смене сбрасываем rows до ответа (WB↔Ozon на «Поставки»). */
  const rowsScopeKeyRef = useRef('');
  /** last sync-at по account_id (ms), чтобы не дёргать Ozon/Yandex при каждом переключении вкладки. */
  const ozonCarriageSyncAtRef = useRef(new Map());
  const yandexShipmentSyncAtRef = useRef(new Map());
  const safeTab = resolveVisibleTabIndex(tabs, tabKey);
  const current = tabs[safeTab] || ALL_TABS[0];

  useEffect(() => {
    if (!tabs.some((item) => fulfillmentTabKey(item) === tabKey)) {
      setTabKey(fulfillmentTabKey(tabs[0] || ALL_TABS[0]));
    }
  }, [tabKey, tabs]);

  useEffect(() => {
    const timer = setInterval(() => setMinuteNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  const stopMsSyncPolling = useCallback(() => {
    if (msSyncPollRef.current) {
      clearInterval(msSyncPollRef.current);
      msSyncPollRef.current = null;
    }
    if (msSyncHideTimerRef.current) {
      clearTimeout(msSyncHideTimerRef.current);
      msSyncHideTimerRef.current = null;
    }
  }, []);

  const startMsSyncPolling = useCallback((jobId, seed = null, options = {}) => {
    const id = String(jobId || '').trim();
    if (!id) return;
    const onComplete = typeof options.onComplete === 'function' ? options.onComplete : null;
    stopMsSyncPolling();
    const kind = seed?.kind || 'ms_sync';
    setMsSync({
      job_id: id,
      kind,
      total: Number(seed?.total) || 0,
      done: Number(seed?.done) || 0,
      failed: Number(seed?.failed) || 0,
      status: seed?.status || 'running',
      message: seed?.message || null,
      has_file: Boolean(seed?.has_file),
      filename: seed?.filename || null,
    });

    let finished = false;
    let lastDone = Number(seed?.done) || 0;
    let lastFailed = Number(seed?.failed) || 0;
    const labels = () => msSyncKindLabel(kind);

    const finishWithData = async (data) => {
      if (finished) return;
      finished = true;
      stopMsSyncPolling();
      const failed = Number(data.failed) || 0;
      const total = Number(data.total) || 0;
      const text = labels();
      setMsSync(data);
      if (data.status === 'failed') {
        notifyError(data.error_message || text.doneFail);
      } else if (!onComplete && failed > 0) {
        notifySuccess(text.donePartial(failed, total));
      } else if (!onComplete) {
        notifySuccess(text.doneOk);
      }
      if (onComplete) {
        try {
          await onComplete(data);
        } catch (completeError) {
          notifyError(apiError(completeError) || text.doneFail);
        }
      }
      loadTabCountsRef.current?.();
      loadRowsRef.current?.({ silent: true });
      msSyncHideTimerRef.current = setTimeout(() => {
        setMsSync(null);
        msSyncHideTimerRef.current = null;
      }, 2500);
    };

    const poll = async () => {
      if (finished) return;
      try {
        const { data } = await axios.get(
          `/fulfillment/unified/jobs/${encodeURIComponent(id)}`,
          { silent: true },
        );
        if (finished) return;
        setMsSync(data);
        const done = Number(data?.done) || 0;
        const failed = Number(data?.failed) || 0;
        if (data?.status === 'running' && (done !== lastDone || failed !== lastFailed)) {
          lastDone = done;
          lastFailed = failed;
          loadRowsRef.current?.({ silent: true });
        }
        if (data?.status === 'completed' || data?.status === 'failed') {
          await finishWithData(data);
        }
      } catch (requestError) {
        if (finished) return;
        if (requestError?.response?.status === 404) {
          finished = true;
          stopMsSyncPolling();
          setMsSync(null);
          if (onComplete) {
            try {
              await onComplete({ job_id: id, status: 'failed' });
            } catch {
              // ignore
            }
          }
        }
      }
    };

    poll().then(() => {
      if (finished) return;
      msSyncPollRef.current = setInterval(poll, 1500);
    });
  }, [notifyError, notifySuccess, stopMsSyncPolling]);

  useEffect(() => () => stopMsSyncPolling(), [stopMsSyncPolling]);

  const loadAccounts = useCallback(async () => {
    const { data } = await axios.get('/fulfillment/unified/accounts');
    const items = data?.items || [];
    setAccounts(items);
    setAccountId((old) => {
      if (!deepLinkRef.current.applied) {
        deepLinkRef.current.applied = true;
        const picked = pickFulfillmentAccount(items, deepLinkRef.current);
        if (picked) return picked;
      }
      if (items.some((item) => String(item.id) === String(old))) return old;
      return items[0]?.id ? String(items[0].id) : '';
    });
  }, []);

  const loadWarehouses = useCallback(async () => {
    if (!accountId || !isWbAccount) {
      setWarehouseNames({});
      return;
    }
    try {
      const { data } = await axios.get('/fulfillment/warehouses', {
        params: { account_id: accountId },
        silent: true,
      });
      const names = Object.fromEntries(
        (data?.items || []).map((item) => [String(item.id), item.name]),
      );
      setWarehouseNames(names);
      setWarehouseId((prev) => (prev && names[String(prev)] != null ? prev : ''));
    } catch {
      setWarehouseNames({});
    }
  }, [accountId, isWbAccount]);

  const loadOzonWarehouses = useCallback(async () => {
    if (!accountId || isWbAccount) {
      setOzonWarehouses([]);
      return;
    }
    try {
      const { data } = await axios.get('/fulfillment/unified/warehouses', {
        params: { account_id: accountId, marketplace: accountMarketplace },
        silent: true,
      });
      const items = data?.items || [];
      setOzonWarehouses(items);
      setWarehouseId((prev) => (
        prev && items.some((item) => String(item.warehouse_id) === String(prev))
          ? prev
          : ''
      ));
    } catch {
      setOzonWarehouses([]);
    }
  }, [accountId, accountMarketplace, isWbAccount]);

  const warehouseNameMap = useMemo(() => {
    if (isWbAccount) return warehouseNames;
    return Object.fromEntries(
      ozonWarehouses.map((warehouse) => [
        String(warehouse.warehouse_id),
        warehouse.warehouse_name || `Склад №${warehouse.warehouse_id}`,
      ]),
    );
  }, [isWbAccount, warehouseNames, ozonWarehouses]);

  const taskListFilterParams = useMemo(() => {
    const productParams = buildFilterParams(filters);
    const yandexDateParams = (() => {
      if (!isYandexAccount) return {};
      const from = String(shipmentDateFrom || '').trim();
      const to = String(shipmentDateTo || '').trim();
      if (from && to && from === to) return { shipment_date: from };
      return {
        ...(from ? { shipment_date_from: from } : {}),
        ...(to ? { shipment_date_to: to } : {}),
      };
    })();
    return {
      ...productParams,
      ...(isWbAccount && cargoType !== '' ? { cargo_type: cargoType } : {}),
      ...(warehouseId !== '' ? { warehouse_id: warehouseId } : {}),
      ...yandexDateParams,
    };
  }, [cargoType, filters, isWbAccount, isYandexAccount, shipmentDateFrom, shipmentDateTo, warehouseId]);

  const loadTabCounts = useCallback(async () => {
    if (!accountId) {
      setTabCounts({
        new: null, waiting_assembly: null, assembly: null, collected: null,
      });
      return;
    }
    try {
      const common = {
        account_id: accountId,
        marketplace: accountMarketplace,
        page: 0,
        page_size: 1,
        q: query || undefined,
        ...taskListFilterParams,
      };
      const withSupply = { ...common, supply_id: isWbAccount ? (supplyId || undefined) : undefined };
      const [
        newResponse,
        waitingResponse,
        assemblyResponse,
        collectedResponse,
      ] = await Promise.all([
        axios.get('/fulfillment/unified/tasks', { params: { ...common, status_group: 'new' }, silent: true }),
        axios.get('/fulfillment/unified/tasks', { params: { ...withSupply, status_group: 'waiting_assembly' }, silent: true }),
        axios.get('/fulfillment/unified/tasks', { params: { ...withSupply, status_group: 'assembly' }, silent: true }),
        axios.get('/fulfillment/unified/tasks', { params: { ...withSupply, status_group: 'collected' }, silent: true }),
      ]);
      setTabCounts({
        new: Number(newResponse.data?.total) || 0,
        waiting_assembly: Number(waitingResponse.data?.total) || 0,
        assembly: Number(assemblyResponse.data?.total) || 0,
        collected: Number(collectedResponse.data?.total) || 0,
      });
    } catch {
      setTabCounts({
        new: null, waiting_assembly: null, assembly: null, collected: null,
      });
    }
  }, [accountId, accountMarketplace, isWbAccount, query, supplyId, taskListFilterParams]);
  loadTabCountsRef.current = loadTabCounts;

  const loadOpenSupplies = useCallback(async () => {
    if (!accountId || !isWbAccount || current.statusGroup !== 'waiting_assembly') {
      setOpenSupplies([]);
      setTargetSupplyId('');
      return;
    }
    try {
      const { data } = await axios.get('/fulfillment/supplies', {
        params: { account_id: accountId, done: 0, page: 0, page_size: 200 },
        silent: true,
      });
      const items = (data?.items || []).filter((item) => (
        !item.done
        && !item.closed_at
        && item.normalized_status?.code === 'forming'
      ));
      setOpenSupplies(items);
      setTargetSupplyId((old) => (
        items.some((item) => String(item.wb_supply_id) === String(old))
          ? old
          : (items[0]?.wb_supply_id || '')
      ));
    } catch {
      setOpenSupplies([]);
      setTargetSupplyId('');
    }
  }, [accountId, current.statusGroup, isWbAccount]);

  // WB: заказы юрлиц и обычные нельзя класть в одну поставку.
  const selectedB2b = isWbAccount
    ? selectionB2bType([...selectedTasks].map((id) => selectedTaskRows.current.get(String(id))))
    : { type: null, mixed: false };
  const targetSupply = openSupplies.find((item) => String(item.wb_supply_id) === String(targetSupplyId)) || null;
  const targetSupplyB2bConflict = supplyB2bConflict(targetSupply, selectedB2b.type);

  useEffect(() => {
    if (selectedB2b.type == null) return;
    setTargetSupplyId((old) => {
      const oldSupply = openSupplies.find((item) => String(item.wb_supply_id) === String(old));
      if (oldSupply && !supplyB2bConflict(oldSupply, selectedB2b.type)) return old;
      const fit = openSupplies.find((item) => !supplyB2bConflict(item, selectedB2b.type));
      return fit ? fit.wb_supply_id : old;
    });
  }, [openSupplies, selectedB2b.type]);

  const loadWriteState = useCallback(async () => {
    if (!accountId || !canExecute) {
      setWriteState(null);
      return;
    }
    try {
      const { data } = await axios.get('/fulfillment/unified/write-state', {
        params: {
          account_id: accountId,
          marketplace: accountMarketplace,
        },
        silent: true,
      });
      setWriteState(data);
    } catch (requestError) {
      setWriteState(null);
      setError(apiError(requestError));
    }
  }, [accountId, accountMarketplace, canExecute]);

  const loadRows = useCallback(async ({ silent = false } = {}) => {
    if (!accountId) return;
    if (current.kind === 'pickingJobs' || current.kind === 'pallets' || current.kind === 'loading') {
      if (!silent) {
        setRows([]);
        setTotal(0);
      }
      return;
    }
    if (!isWbAccount && current.kind === 'reshipment') {
      if (!silent) {
        setRows([]);
        setTotal(0);
      }
      return;
    }
    const loadGen = silent ? rowsLoadGenRef.current : ++rowsLoadGenRef.current;
    const scopeKey = `${accountMarketplace}:${accountId}:${current.kind}`;
    if (!silent && rowsScopeKeyRef.current !== scopeKey) {
      rowsScopeKeyRef.current = scopeKey;
      // Не показываем чужой список при смене кабинета/МП/вкладки (WB supplies → Ozon carriages).
      setRows([]);
      setTotal(0);
    }
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const useUnifiedTasks = current.kind !== 'supplies' && current.kind !== 'reshipment';
      const endpoint = current.kind === 'supplies'
        ? (isWbAccount ? '/fulfillment/supplies' : '/fulfillment/unified/shipments')
        : (current.kind === 'reshipment'
          ? '/fulfillment/reshipment'
          : '/fulfillment/unified/tasks');
      const listParams = {
        account_id: accountId,
        marketplace: accountMarketplace,
        page,
        page_size: rowsPerPage,
        sort_key: sort.key,
        sort_dir: sort.dir,
        q: current.kind === 'supplies' ? undefined : (query || undefined),
        supply_id: (
          current.kind === 'supplies' || current.statusGroup === 'new' || !isWbAccount
            ? undefined
            : (supplyId || undefined)
        ),
        status_group: current.statusGroup,
        ...(useUnifiedTasks ? taskListFilterParams : {}),
      };

      // Ozon/Yandex «Поставки»: список из unified shipments.
      // Live POST sync — в фоне с TTL, без блокировки UI. Silent-poll таблицу не дёргает.
      if (!silent && current.kind === 'supplies' && isCarriageAccount && accountId) {
        const syncAccountId = Number(accountId);
        const syncRef = isYandexAccount ? yandexShipmentSyncAtRef : ozonCarriageSyncAtRef;
        const lastAt = Number(syncRef.current.get(syncAccountId) || 0);
        const due = Date.now() - lastAt > OZON_CARRIAGE_SYNC_TTL_MS;
        if (due) {
          syncRef.current.set(syncAccountId, Date.now());
          const syncUrl = isYandexAccount
            ? '/fulfillment/yandex/shipments/sync'
            : '/fulfillment/ozon/carriages/sync';
          axios.post(syncUrl, {
            account_id: syncAccountId,
          }, { silent: true })
            .then(async () => {
              if (rowsLoadGenRef.current !== loadGen) return;
              try {
                const { data: refreshed } = await axios.get('/fulfillment/unified/shipments', {
                  params: listParams,
                  silent: true,
                });
                if (rowsLoadGenRef.current !== loadGen) return;
                setRows(refreshed?.items || []);
                setTotal(Number(refreshed?.total) || 0);
                if (isOzonAccount) {
                  const methods = Array.isArray(refreshed?.pending_methods)
                    ? refreshed.pending_methods
                    : [];
                  setOzonPendingMethods(methods);
                  setOzonCreateMethodId((prev) => {
                    if (prev && methods.some((m) => String(m.delivery_method_id) === String(prev))) {
                      return prev;
                    }
                    return methods[0]?.delivery_method_id != null
                      ? String(methods[0].delivery_method_id)
                      : '';
                  });
                }
              } catch {
                // Тихий refresh после sync — best-effort.
              }
            })
            .catch((syncError) => {
              syncRef.current.delete(syncAccountId);
              console.warn('[fulfillment] shipment sync', syncError?.message || syncError);
            });
        }
      }

      const { data } = await axios.get(endpoint, {
        params: listParams,
        ...(silent ? { silent: true } : {}),
      });
      if (rowsLoadGenRef.current !== loadGen) return;
      setRows(data?.items || []);
      setTotal(Number(data?.total) || 0);
      if (current.kind === 'supplies' && isOzonAccount) {
        const methods = Array.isArray(data?.pending_methods) ? data.pending_methods : [];
        setOzonPendingMethods(methods);
        setOzonCreateMethodId((prev) => {
          if (prev && methods.some((m) => String(m.delivery_method_id) === String(prev))) {
            return prev;
          }
          return methods[0]?.delivery_method_id != null
            ? String(methods[0].delivery_method_id)
            : '';
        });
      } else if (current.kind === 'supplies' && isYandexAccount) {
        setOzonPendingMethods([]);
      } else if (current.kind !== 'supplies') {
        setOzonPendingMethods([]);
      }
    } catch (requestError) {
      if (rowsLoadGenRef.current !== loadGen) return;
      if (!silent) setError(apiError(requestError));
    } finally {
      if (!silent && rowsLoadGenRef.current === loadGen) {
        setLoading(false);
      }
    }
  }, [accountId, accountMarketplace, current, isCarriageAccount, isOzonAccount, isWbAccount, isYandexAccount, page, query, rowsPerPage, sort, supplyId, taskListFilterParams]);
  loadRowsRef.current = loadRows;

  useEffect(() => {
    loadAccounts().catch((requestError) => setError(apiError(requestError)));
  }, [loadAccounts]);

  // Синхронизация deep-link ?mp=&account_id= с выбранным кабинетом (bookmarks / redirect с /fulfillment/ozon).
  useEffect(() => {
    if (!accountId || !selectedAccount) return;
    const curId = searchParams.get('account_id') || '';
    const curMp = normalizeFulfillmentMp(searchParams.get('mp'));
    if (String(curId) === String(accountId) && curMp === accountMarketplace) return;
    const next = new URLSearchParams(searchParams);
    next.set('account_id', String(accountId));
    next.set('mp', accountMarketplace);
    setSearchParams(next, { replace: true });
  }, [accountId, accountMarketplace, selectedAccount, searchParams, setSearchParams]);

  useEffect(() => {
    loadRows();
    loadWriteState();
  }, [loadRows, loadWriteState]);

  useEffect(() => {
    loadWarehouses();
    loadOzonWarehouses();
    loadTabCounts();
  }, [loadTabCounts, loadOzonWarehouses, loadWarehouses]);

  useEffect(() => {
    loadOpenSupplies();
  }, [loadOpenSupplies]);

  // ProductFilters грузит группы/подгруппы по mp — держим mp в sync с кабинетом.
  useEffect(() => {
    const nextMp = filtersMp;
    setFilters((prev) => (prev.mp === nextMp ? prev : {
      ...prev,
      mp: nextMp,
      gruppa: [],
      gruppaExcluded: [],
      podgruppa: [],
      podgruppaExcluded: [],
      selectedArticle: null,
    }));
    // Смена МП сбрасывает type-фильтры (cargo ↔ склад).
    setCargoType('');
    setWarehouseId('');
  }, [accountMarketplace, filtersMp]);

  const handleFilters = (next) => {
    setFilters({ ...next, mp: filtersMp });
    setPage(0);
  };

  const handleTab = (_event, value) => {
    const next = tabs[value];
    if (!next) return;
    setTabKey(fulfillmentTabKey(next));
    setSelectedTasks(new Set());
    setInlineSupplyCreate(false);
    setSupplyName('');
    setPage(0);
    setSort(defaultTaskSort(next.statusGroup));
  };

  const handleSort = (key) => {
    setPage(0);
    setSort((old) => ({
      key,
      dir: old.key === key && old.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const operationKey = useCallback((signature) => {
    if (!operationKeys.current.has(signature)) {
      operationKeys.current.set(signature, idempotencyKey(signature.replace(/[^a-z0-9-]/gi, '-').slice(0, 50)));
    }
    return operationKeys.current.get(signature);
  }, []);

  const runAction = async (name, request, successText, signature) => {
    setAction(name);
    setError('');
    setNotice('');
    try {
      const response = await request();
      if (signature) operationKeys.current.delete(signature);
      if (successText) setNotice(successText);
      await loadRows();
      await loadTabCounts();
      await loadWriteState();
      return response?.data;
    } catch (requestError) {
      setError(apiError(requestError));
      return null;
    } finally {
      setAction('');
    }
  };

  const reportBulkResult = (result, ids, prefix = '') => {
    const summary = summarizeBulkResult(result, ids);
    const notice = formatBulkNotice(summary);
    const text = prefix ? `${prefix}\n${notice.text}` : notice.text;
    setNotice(text);
    if (notice.severity === 'error') notifyError(text);
    else if (notice.severity === 'warning') notifyWarning(text);
    else notifySuccess(text);
    return summary;
  };

  const startInlineSupplyCreate = () => {
    setInlineSupplyCreate(true);
    setSupplyName(`Поставка ${new Date().toLocaleDateString('ru-RU')}`);
  };

  const cancelInlineSupplyCreate = () => {
    setInlineSupplyCreate(false);
    setSupplyName('');
  };

  const createSupply = async () => {
    const name = supplyName.trim();
    if (!name) return;
    // Новая поставка из «Ожидают сборки» сразу получает тип выбранных заказов.
    const b2bType = inlineSupplyCreate && !selectedB2b.mixed ? selectedB2b.type : null;
    const signature = `create:${accountId}:${name}:${b2bType ?? ''}`;
    const result = await runAction(
      'create',
      () => axios.post('/fulfillment/supplies', {
        account_id: Number(accountId),
        name,
        ...(b2bType == null ? {} : { is_b2b: b2bType }),
      }, { headers: { 'Idempotency-Key': operationKey(signature) } }),
      'Поставка создана',
      signature,
    );
    if (result) {
      setCreateOpen(false);
      setInlineSupplyCreate(false);
      setSupplyName('');
      await loadOpenSupplies();
      if (result.supply_id) setTargetSupplyId(String(result.supply_id));
    }
  };

  const loadDeliverOptions = async (supplyId, city, preserveForm) => {
    setShippingLoading(true);
    setShippingError('');
    try {
      const { data } = await axios.get(
        `/fulfillment/supplies/${encodeURIComponent(supplyId)}/shipping-options`,
        {
          params: {
            account_id: Number(accountId),
            ...(city ? { city } : {}),
          },
        },
      );
      setShippingForm(normalizeShippingForm({
        ...data,
        supplies: [{
          supply_id: supplyId,
          cargo_type: data.cargo_type,
          shipping_point_id: data.shipping_point_id,
          points: data.points || [],
        }],
      }, { preserve: preserveForm }));
    } catch (requestError) {
      setShippingForm(null);
      setShippingError(apiError(requestError));
    } finally {
      setShippingLoading(false);
    }
  };

  const openDeliver = (supply) => {
    setDeliverTarget(supply);
    setShippingForm(null);
    setShippingError('');
    loadDeliverOptions(supply.wb_supply_id);
  };

  const closeDeliver = () => {
    if (action) return;
    setDeliverTarget(null);
    setShippingForm(null);
    setShippingError('');
  };

  const confirmDeliver = async () => {
    const id = deliverTarget?.wb_supply_id;
    if (!id || !shippingFormReady(shippingForm)) return;
    const body = shippingConfirmBody(shippingForm);
    const first = (body.supplies || [])[0] || {};
    const signature = `deliver:${accountId}:${id}`;
    const result = await runAction(
      `deliver:${id}`,
      () => axios.post(`/fulfillment/supplies/${encodeURIComponent(id)}/deliver`, {
        account_id: Number(accountId),
        shipping_city: body.city,
        shipping_point_id: first.shipping_point_id,
        shipping_dt: body.shipping_dt,
        shipping_type: body.shipping_type,
      }, { headers: { 'Idempotency-Key': operationKey(signature) } }),
      'Поставка передана в доставку',
      signature,
    );
    if (result) {
      setDeliverTarget(null);
      setShippingForm(null);
      setShippingError('');
      await loadOpenSupplies();
    }
  };

  const deleteSupply = (id) => {
    setConfirm({
      title: 'Удалить поставку',
      message: `Удалить поставку ${id} в WB? Действие необратимо.`,
      confirmText: 'Удалить',
      onConfirm: () => {
        setConfirm(null);
        const signature = `delete:${accountId}:${id}`;
        runAction(
          `delete:${id}`,
          () => axios.delete(`/fulfillment/supplies/${encodeURIComponent(id)}`, {
            params: { account_id: Number(accountId) },
            headers: { 'Idempotency-Key': operationKey(signature) },
          }),
          'Пустая поставка удалена',
          signature,
        );
      },
    });
  };

  const openJournal = async (task) => {
    const externalId = taskExternalId(task);
    setJournal({ open: true, task, events: [], loading: true });
    try {
      const { data } = await axios.get(
        `/fulfillment/unified/tasks/${encodeURIComponent(externalId)}/events`,
        { params: { account_id: Number(accountId), limit: 100 } },
      );
      setJournal({ open: true, task, events: data?.items || [], loading: false });
    } catch (requestError) {
      setJournal((old) => ({ ...old, loading: false }));
      setError(apiError(requestError));
    }
  };

  const projectToMs = async (task) => {
    const marketplace = taskMarketplace(task);
    const externalId = taskExternalId(task);
    const actionKey = `ms:${externalId}`;
    setAction(actionKey);
    setError('');
    setNotice('');
    try {
      const { data } = await axios.post(
        `/fulfillment/unified/tasks/${encodeURIComponent(externalId)}/ms-push`,
        {
          account_id: Number(accountId),
          marketplace,
        },
      );
      const msg = data?.created
        ? 'Заказ создан в МойСклад'
        : (data?.linked_existing
          ? 'Заказ связан с МойСклад'
          : (data?.skipped
            ? (data.reason === 'ms_order_not_found'
              ? 'В МойСклад заказ не найден'
              : 'Связь с МойСклад пропущена')
            : 'Связь с МойСклад обновлена'));
      setNotice(msg);
      if (data?.ms_id) notifySuccess(msg);
      else if (data?.skipped) notifyError(msg);
      else notifySuccess(msg);
      // Точечно обновляем строку — без полного reload списка/счётчиков вкладок.
      if (data?.ms_id) {
        setRows((prev) => prev.map((row) => (
          taskExternalId(row) === externalId
            ? { ...row, ms_id: data.ms_id, ms_href: data.ms_href || row.ms_href || null }
            : row
        )));
      }
      return data;
    } catch (requestError) {
      const message = apiError(requestError);
      setError(message);
      notifyError(message);
      return null;
    } finally {
      setAction('');
    }
  };

  const moveSelectedToAssembly = async () => {
    const ids = [...selectedTasks];
    const signature = isOzonAccount
      ? `unified-assembly:${accountId}:${ids.slice().sort().join(',')}`
      : null;
    const result = await runAction(
      'manual-assembly',
      () => axios.post('/fulfillment/unified/tasks/assembly', {
        account_id: Number(accountId),
        marketplace: accountMarketplace,
        external_ids: ids,
      }, signature
        ? { headers: { 'Idempotency-Key': operationKey(signature) } }
        : undefined),
      '',
      signature || undefined,
    );
    if (result) {
      reportBulkResult(result, ids);
      setSelectedTasks(new Set());
      if (result.ms_sync_job_id) {
        startMsSyncPolling(result.ms_sync_job_id, { kind: 'ms_sync', ...result });
      }
    }
  };

  const moveSelectedToNew = async () => {
    const ids = [...selectedTasks];
    const signature = isOzonAccount
      ? `unified-return-new:${accountId}:${ids.slice().sort().join(',')}`
      : null;
    const result = await runAction(
      'manual-new',
      () => axios.post('/fulfillment/unified/tasks/new', {
        account_id: Number(accountId),
        marketplace: accountMarketplace,
        external_ids: ids,
      }, signature
        ? { headers: { 'Idempotency-Key': operationKey(signature) } }
        : undefined),
      '',
      signature || undefined,
    );
    if (result) {
      reportBulkResult(result, ids);
      setSelectedTasks(new Set());
      if (result.ms_sync_job_id) {
        startMsSyncPolling(result.ms_sync_job_id, { kind: 'ms_sync', ...result });
      }
    }
  };

  const printOzonCarriageBarcode = async (row) => {
    const carriageId = row?.carriage_id ?? row?.shipment_id ?? row?.external_id;
    if (!carriageId) {
      setError('Нет ID отгрузки (carriage)');
      return;
    }
    if (!canUsePrintQueue || !printer.selectedPrinter?.printer_uid) {
      setError('Выберите принтер в Настройки → Печать');
      notifyError('Выберите принтер в Настройки → Печать');
      return;
    }
    await runAction(
      `print-carriage:${carriageId}`,
      () => axios.post(`/fulfillment/ozon/carriages/${carriageId}/barcode-print`, {
        account_id: Number(accountId),
        printer_uid: printer.selectedPrinter.printer_uid,
      }),
      `Штрихкод отгрузки ${carriageId} отправлен в печать`,
    );
  };

  const downloadYandexShipmentAct = async (row) => {
    const shipmentId = row?.carriage_id ?? row?.shipment_id ?? row?.external_id;
    if (!shipmentId) {
      setError('Нет ID поставки');
      return;
    }
    const actionName = `download-act:${shipmentId}`;
    setAction(actionName);
    setError('');
    setNotice('');
    try {
      const { data: pdfBlob } = await axios.get(yandexShipmentActPath(shipmentId), {
        params: { account_id: Number(accountId) },
        responseType: 'blob',
      });
      const contentType = String(pdfBlob?.type || '');
      if (contentType.includes('application/json')) {
        const text = await pdfBlob.text();
        let message = 'Не удалось скачать акт';
        try {
          const parsed = JSON.parse(text);
          if (parsed?.message) message = parsed.message;
        } catch {
          // keep default
        }
        throw new Error(message);
      }
      const filename = `yandex-act-${shipmentId}.pdf`;
      openOrDownloadPdfBlob(pdfBlob, filename);
      notifySuccess(`Акт сохранён: ${filename}`);
    } catch (requestError) {
      let message = apiError(requestError);
      const data = requestError?.response?.data;
      if (typeof Blob !== 'undefined' && data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          if (parsed?.message) message = parsed.message;
        } catch {
          // keep apiError message
        }
      } else if (requestError?.message && !requestError?.response) {
        message = requestError.message;
      }
      setError(message);
      notifyError(message);
    } finally {
      setAction('');
    }
  };

  const approveOzonCarriage = async (row) => {
    const carriageId = row?.carriage_id ?? row?.shipment_id ?? row?.external_id;
    if (!carriageId) {
      setError('Нет ID отгрузки (carriage)');
      return;
    }
    setConfirm({
      title: 'Подтвердить отгрузку',
      message: 'Подтвердить отгрузку в маркетплейсе? После подтверждения состав изменить нельзя.',
      confirmText: 'Подтвердить',
      onConfirm: () => {
        setConfirm(null);
        runApproveCarriage(carriageId);
      },
    });
  };

  const runApproveCarriage = async (carriageId) => {
    const url = isYandexAccount
      ? yandexShipmentClosePath(carriageId)
      : `/fulfillment/ozon/carriages/${carriageId}/approve`;
    const result = await runAction(
      `approve-carriage:${carriageId}`,
      () => axios.post(url, isYandexAccount
        ? { account_id: Number(accountId) }
        : {
          account_id: Number(accountId),
          printer_uid: printer.selectedPrinter?.printer_uid || undefined,
          fetch_act: true,
        }),
      `Отгрузка ${carriageId} закрыта`,
    );
    if (result) await loadRows();
  };

  const addSelectedToSupply = async () => {
    const ids = [...selectedTasks];
    if (ids.length > MAX_ASSEMBLY_BATCH) {
      setError(`За один раз можно добавить не более ${MAX_ASSEMBLY_BATCH} заказов`);
      return;
    }
    const signature = `add:${accountId}:${targetSupplyId}:${ids.slice().sort().join(',')}`;
    const result = await runAction(
      'add-to-supply',
      () => axios.post(`/fulfillment/supplies/${encodeURIComponent(targetSupplyId)}/orders`, {
        account_id: Number(accountId),
        orders: ids,
      }, { headers: { 'Idempotency-Key': operationKey(signature) } }),
      '',
      signature,
    );
    if (result) {
      const prefix = result.supply_replaced
        ? `Поставка ${result.replaced_from} закрыта в WB. Заказы добавлены в новую ${result.supply_id}`
        : '';
      reportBulkResult(result, ids, prefix);
      if (result.supply_replaced && result.supply_id) setTargetSupplyId(String(result.supply_id));
      setSelectedTasks(new Set());
      await loadOpenSupplies();
    }
  };

  const shipSelectedToCollected = async () => {
    const ids = [...selectedTasks];
    if (!ids.length || !accountId || !(isOzonAccount || isYandexAccount)) return;
    if (ids.length > MAX_ASSEMBLY_BATCH) {
      setError(`За один раз можно передать не более ${MAX_ASSEMBLY_BATCH} заказов`);
      return;
    }
    const signature = `ship-collected:${accountId}:${ids.slice().sort().join(',')}`;
    setError('');
    try {
      const { data } = await axios.post('/fulfillment/unified/tasks/ship-to-collected', {
        account_id: Number(accountId),
        marketplace: accountMarketplace,
        external_ids: ids,
      }, {
        silent: true,
        headers: { 'Idempotency-Key': operationKey(signature) },
      });
      if (data?.job_id) {
        startMsSyncPolling(data.job_id, data);
        setSelectedTasks(new Set());
        setNotice('Передача в сборку идёт в фоне');
      }
    } catch (requestError) {
      const data = requestError?.response?.data || {};
      if (data.code === 'confirm_in_progress') {
        const running = data.details;
        if (running?.job_id) startMsSyncPolling(running.job_id, running);
        setNotice('Передача в сборку уже выполняется');
        return;
      }
      const message = apiError(requestError);
      setError(message);
      notifyError(message);
    }
  };

  const createAndCloseOzonCarriage = async () => {
    const methodId = Number(ozonCreateMethodId);
    if (!accountId || !Number.isInteger(methodId) || methodId <= 0) {
      setError('Выберите способ доставки для отгрузки');
      return;
    }
    const signature = `create-carriage:${accountId}:${methodId}`;
    const result = await runAction(
      'create-ozon-carriage',
      () => axios.post('/fulfillment/ozon/carriages', {
        account_id: Number(accountId),
        delivery_method_id: methodId,
        approve: true,
        idempotency_key: operationKey(signature),
      }, { headers: { 'Idempotency-Key': operationKey(signature) } }),
      `Отгрузка создана и закрыта (способ ${methodId})`,
      signature,
    );
    if (result) {
      setOzonCreateOpen(false);
      await loadRows();
      await loadTabCounts();
    }
  };

  const openPickingCreate = async () => {
    setError('');
    try {
      const { data } = await axios.get('/fulfillment/unified/picking-jobs/assignees', {
        params: {
          account_id: Number(accountId),
          marketplace: accountMarketplace,
        },
      });
      const items = data?.items || [];
      setPickingAssignees(items);
      setPickingAssignee(items[0]?.id || '');
      setPickingName(`Подбор ${new Date().toLocaleDateString('ru-RU')}`);
      setPickingCreateOpen(true);
    } catch (requestError) {
      setError(apiError(requestError));
    }
  };

  const createPickingJob = async () => {
    const ids = [...selectedTasks];
    setAction('create-picking-job');
    setError('');
    try {
      const { data } = await axios.post('/fulfillment/unified/picking-jobs', {
        account_id: Number(accountId),
        marketplace: accountMarketplace,
        name: pickingName,
        assigned_to: pickingAssignee ? Number(pickingAssignee) : undefined,
        external_ids: ids,
        labeling_mode: pickingLabelingMode,
      });
      setNotice(data?.code
        ? `Задание ${data.code} создано: ${ids.length} заказов`
        : `Задание создано: ${ids.length} заказов`);
      setPickingCreateOpen(false);
      setSelectedTasks(new Set());
      setPickingRefreshKey((value) => value + 1);
      await loadRows();
      await loadTabCounts();
    } catch (requestError) {
      setError(apiError(requestError));
    } finally {
      setAction('');
    }
  };

  const formatProductionError = (requestError) => {
    const details = requestError?.response?.data?.details;
    let message = apiError(requestError);
    const failed = Array.isArray(details?.failed) ? details.failed : [];
    if (failed.length) {
      const sample = failed
        .slice(0, 8)
        .map((row) => {
          const orderId = row.wb_order_id || row.ms_product_id || '';
          const name = row.article || row.ms_product_name || '';
          return name ? `${orderId} (${name})` : String(orderId);
        })
        .join(', ');
      if (sample) message = `${message}: ${sample}`;
    }
    return message;
  };

  const formatProductionJobError = (job) => {
    const result = job?.result || {};
    let message = job?.error_message || result.message || 'Не удалось создать производство';
    const failed = Array.isArray(result.details?.failed) ? result.details.failed : [];
    if (failed.length) {
      const sample = failed
        .slice(0, 8)
        .map((row) => {
          const orderId = row.wb_order_id || row.ms_product_id || '';
          const name = row.article || row.ms_product_name || '';
          return name ? `${orderId} (${name})` : String(orderId);
        })
        .join(', ');
      if (sample) message = `${message}: ${sample}`;
    }
    return message;
  };

  const applyProductionJobResult = (job, ids, signature) => {
    const result = job?.result || {};
    if (job?.status === 'failed') {
      const code = result.code;
      const details = result.details;
      if (code === 'ms_techcard_ambiguous' && Array.isArray(details?.items) && details.items.length) {
        openProductionDialog(details.items, ids);
        return;
      }
      const message = formatProductionJobError(job);
      setError(message);
      return;
    }
    const label = result.name || result.production_task_id || 'без номера';
    let msg = result.replayed
      ? `Производство уже создано: ${label}`
      : `Производство создано и запущено: ${label}`;
    if (Array.isArray(result.warnings) && result.warnings.length) {
      msg = `${msg}. ${result.warnings.join(' ')}`;
    }
    setNotice(msg);
    notifySuccess(msg);
    setSelectedTasks(new Set());
    closeProductionDialog();
    operationKeys.current.delete(signature);
  };

  const openProductionDialog = (items, orderIds) => {
    const list = Array.isArray(items) ? items : [];
    const choiceItems = list.filter((item) => item.needs_choice);
    const selections = {};
    for (const item of choiceItems) {
      selections[item.ms_product_id] = item.selected_plan_id || '';
    }
    setProductionDialog({
      open: true,
      items: choiceItems,
      selections,
      orderIds,
      restCount: list.filter((item) => !item.needs_choice).length,
    });
  };

  const closeProductionDialog = () => {
    setProductionDialog({
      open: false,
      items: [],
      selections: {},
      orderIds: [],
      restCount: 0,
    });
  };

  const submitProduction = async (ids, processingPlans = {}) => {
    const sortedIds = ids.map(String).sort((a, b) => a.localeCompare(b, 'ru', { numeric: true }));
    const signature = `create-production:${accountMarketplace}:${accountId}:${sortedIds.join(',')}:${
      Object.entries(processingPlans)
        .sort(([left], [right]) => String(left).localeCompare(String(right)))
        .map(([productId, planId]) => `${productId}=${planId}`)
        .join(';')
    }`;
    setAction('create-production');
    setError('');
    setNotice('');
    try {
      const { data } = await axios.post('/fulfillment/unified/tasks/create-production', {
        account_id: Number(accountId),
        marketplace: accountMarketplace,
        external_ids: ids.map(String),
        processing_plans: processingPlans,
      }, {
        silent: true,
        headers: { 'Idempotency-Key': operationKey(signature) },
      });
      if (data?.job_id) {
        closeProductionDialog();
        setNotice('Создаём производство в МойСклад…');
        startMsSyncPolling(data.job_id, data, {
          onComplete: (job) => applyProductionJobResult(job, ids, signature),
        });
        return;
      }
      applyProductionJobResult({ status: 'completed', result: data }, ids, signature);
    } catch (requestError) {
      const body = requestError?.response?.data || {};
      if (body.code === 'production_in_progress' && body.details?.job_id) {
        closeProductionDialog();
        startMsSyncPolling(body.details.job_id, body.details, {
          onComplete: (job) => applyProductionJobResult(job, ids, signature),
        });
        setNotice('Создание производства уже выполняется');
        return;
      }
      const code = body.code;
      const details = body.details;
      if (code === 'ms_techcard_ambiguous' && Array.isArray(details?.items) && details.items.length) {
        openProductionDialog(details.items, ids);
        return;
      }
      const message = formatProductionError(requestError);
      setError(message);
      notifyError(message);
    } finally {
      setAction('');
    }
  };

  const createProductionFromSelected = async () => {
    const ids = [...selectedTasks];
    if (!ids.length || !accountId) return;
    setAction('create-production');
    setError('');
    setNotice('');
    let preview = null;
    try {
      const { data } = await axios.post('/fulfillment/unified/tasks/create-production/preview', {
        account_id: Number(accountId),
        marketplace: accountMarketplace,
        external_ids: ids.map(String),
      });
      preview = data;
    } catch (requestError) {
      const message = formatProductionError(requestError);
      setError(message);
      notifyError(message);
      setAction('');
      return;
    }
    // Привязанная живая карта (ms_assortment_settings) — сразу create.
    // Без привязки или архивный bind — диалог выбора, затем одно задание на весь пул.
    if (preview?.needs_choice) {
      openProductionDialog(preview.items || [], ids);
      setAction('');
      return;
    }
    await submitProduction(ids, {});
  };

  const confirmProductionDialog = async () => {
    const ids = productionDialog.orderIds;
    if (!ids.length) return;
    const processingPlans = {};
    for (const item of productionDialog.items) {
      const planId = String(productionDialog.selections[item.ms_product_id] || '').trim();
      if (!planId) return;
      processingPlans[item.ms_product_id] = planId;
    }
    await submitProduction(ids, processingPlans);
  };

  const enqueuePrint = async (documentType, sourceIds, actionName) => {
    const ids = [...new Set((Array.isArray(sourceIds) ? sourceIds : [sourceIds])
      .map((value) => String(value || '').trim())
      .filter(Boolean))];
    if (!ids.length) return null;
    if (!printer.selectedPrinter) {
      notifyError(PRINTER_NOT_SELECTED_MSG);
      return null;
    }
    setPrintAction(actionName);
    try {
      await printClient.createJobs({
        accountId,
        printerUid: printer.selectedPrinter.printer_uid,
        documentType,
        sourceIds: ids,
        idempotencyKey: newPrintIdempotencyKey(`${documentType}-${accountId}`),
      });
      notifySuccess('Успешно напечатано');
      return true;
    } catch (requestError) {
      notifyError(apiError(requestError));
      if (requestError?.response?.data?.code === 'printer_offline') await printer.refresh();
      return null;
    } finally {
      setPrintAction('');
    }
  };

  const labelsPdfFilename = (count, mode) => {
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const prefix = isYandexAccount ? 'yandex-labels' : (isOzonAccount ? 'ozon-labels' : 'wb-labels');
    const suffix = mode === 'mass' ? 'mass' : 'single';
    return `${prefix}-${suffix}-${count}-${stamp}.pdf`;
  };

  /** Скачивание PDF этикеток АРМ (без очереди печати). mode=mass|labels. */
  const downloadLabelsPdf = async (sourceIds, mode, actionName) => {
    const ids = [...new Set((Array.isArray(sourceIds) ? sourceIds : [sourceIds])
      .map((value) => String(value || '').trim())
      .filter(Boolean))];
    if (!ids.length) return null;
    setPrintAction(actionName);
    const filename = labelsPdfFilename(ids.length, mode);

    const savePdfBlob = async (pdfBlob, name) => {
      const contentType = String(pdfBlob?.type || '');
      if (contentType.includes('application/json')) {
        const text = await pdfBlob.text();
        let message = 'Не удалось сформировать PDF этикеток';
        try {
          const parsed = JSON.parse(text);
          if (parsed?.message) message = parsed.message;
        } catch {
          // keep default
        }
        throw new Error(message);
      }
      openOrDownloadPdfBlob(pdfBlob, name);
      notifySuccess(`PDF сохранён: ${name}`);
      return true;
    };

    const waitAndDownloadJob = (job) => new Promise((resolve) => {
      startMsSyncPolling(job.job_id, job, {
        onComplete: async (data) => {
          if (data.status !== 'completed') {
            resolve(null);
            return;
          }
          try {
            const { data: pdfBlob } = await axios.get(
              `/fulfillment/unified/jobs/${encodeURIComponent(data.job_id)}/file`,
              { responseType: 'blob' },
            );
            const name = data.filename || filename;
            await savePdfBlob(pdfBlob, name);
            resolve(true);
          } catch (downloadError) {
            resolve(null);
            throw downloadError;
          }
        },
      });
    });

    try {
      if (isYandexAccount) {
        try {
          const { data } = await axios.post('/fulfillment/unified/labels/pdf', {
            account_id: Number(accountId),
            marketplace: accountMarketplace,
            external_ids: ids,
            mode,
          }, { silent: true });
          if (data?.job_id) {
            const ok = await waitAndDownloadJob(data);
            return ok;
          }
          throw new Error('Не удалось запустить получение этикеток Яндекса');
        } catch (requestError) {
          const body = requestError?.response?.data;
          if (requestError?.response?.status === 409 && body?.details?.foreign) {
            const message = 'Другой оператор уже формирует этикетки по этому кабинету. Повторите через минуту';
            notifyError(message);
            return null;
          }
          if (requestError?.response?.status === 409 && body?.details?.job_id) {
            const ok = await waitAndDownloadJob(body.details);
            return ok;
          }
          throw requestError;
        }
      }

      const { data: pdfBlob } = await axios.post('/fulfillment/unified/labels/pdf', {
        account_id: Number(accountId),
        marketplace: accountMarketplace,
        external_ids: ids,
        mode,
      }, { responseType: 'blob' });
      return savePdfBlob(pdfBlob, filename);
    } catch (requestError) {
      let message = apiError(requestError);
      const data = requestError?.response?.data;
      if (typeof Blob !== 'undefined' && data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          if (parsed?.message) message = parsed.message;
        } catch {
          // keep apiError message
        }
      } else if (requestError?.message && !requestError?.response) {
        message = requestError.message;
      }
      notifyError(message);
      return null;
    } finally {
      setPrintAction('');
    }
  };

  /** Скачать этикетку строки: WB sticker / Ozon package-label / Yandex label (PDF). */
  const printTaskLabel = async (task) => {
    const sourceId = taskSourceId(task) || taskExternalId(task);
    if (!sourceId) return null;
    return downloadLabelsPdf(sourceId, 'labels', `sticker:${sourceId}`);
  };

  const printSelectedStickers = async () => {
    const orderedSourceIds = orderSelectedSourceIds(
      [...selectedTaskRows.current.values(), ...rows],
      selectedTasks,
    );
    if (!orderedSourceIds.length) return;
    const ok = await downloadLabelsPdf(orderedSourceIds, 'mass', 'batch-stickers');
    if (ok) setSelectedTasks(new Set());
  };

  const fetchAllTasksForPickingList = async (statusGroup) => {
    const pageSize = MAX_TASKS_PAGE_SIZE;
    const items = [];
    let pageIdx = 0;
    let totalCount = Infinity;
    while (items.length < totalCount) {
      const { data } = await axios.get('/fulfillment/unified/tasks', {
        params: {
          account_id: accountId,
          marketplace: accountMarketplace,
          page: pageIdx,
          page_size: pageSize,
          sort_key: 'article',
          sort_dir: 'asc',
          q: query || undefined,
          supply_id: isWbAccount ? (supplyId || undefined) : undefined,
          status_group: statusGroup,
          ...taskListFilterParams,
        },
      });
      const chunk = data?.items || [];
      totalCount = Number(data?.total) || 0;
      items.push(...chunk);
      if (!chunk.length || chunk.length < pageSize) break;
      pageIdx += 1;
      if (pageIdx > 50) break;
    }
    return items;
  };

  const printPickingList = async () => {
    if (!accountId || pickingListPrinting) return;
    setPickingListPrinting(true);
    setError('');
    try {
      const statusGroup = current.statusGroup === 'new' ? 'new' : 'waiting_assembly';
      let sourceTasks = [];
      if (selectedTasks.size > 0) {
        const fromCache = [...selectedTasks]
          .map((id) => selectedTaskRows.current.get(String(id)))
          .filter(Boolean);
        if (fromCache.length === selectedTasks.size) {
          sourceTasks = fromCache;
        } else {
          const all = await fetchAllTasksForPickingList(statusGroup);
          const selected = new Set([...selectedTasks].map(String));
          sourceTasks = all.filter((task) => selected.has(taskExternalId(task)));
        }
      } else {
        sourceTasks = await fetchAllTasksForPickingList(statusGroup);
      }
      if (!sourceTasks.length) {
        notifyError('Нет заказов для листа подбора');
        return;
      }
      const listRows = aggregatePickingListByArticle(sourceTasks);
      const accountName = accounts.find((item) => String(item.id) === String(accountId))?.name || '';
      const printedAt = new Date();
      const filename = pickingListFilename(printedAt);
      const { data: pdfBlob } = await axios.post('/fulfillment/picking-list-pdf', {
        account_name: accountName,
        printed_at: printedAt.toISOString(),
        order_count: sourceTasks.length,
        rows: listRows,
      }, { responseType: 'blob' });
      const contentType = String(pdfBlob?.type || '');
      if (contentType.includes('application/json')) {
        const text = await pdfBlob.text();
        let message = 'Не удалось сформировать PDF листа подбора';
        try {
          const parsed = JSON.parse(text);
          if (parsed?.message) message = parsed.message;
        } catch {
          // keep default
        }
        throw new Error(message);
      }
      openOrDownloadPdfBlob(pdfBlob, filename);
      notifySuccess(`PDF сохранён: ${filename}`);
    } catch (requestError) {
      let message = apiError(requestError);
      const data = requestError?.response?.data;
      if (typeof Blob !== 'undefined' && data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          if (parsed?.message) message = parsed.message;
        } catch {
          // keep apiError message
        }
      }
      setError(message);
      notifyError(message);
    } finally {
      setPickingListPrinting(false);
    }
  };

  // Ozon: локальные assembly/picking не зависят от write-toggle; WB write и Ozon carriage — да.
  const mpWriteBlocked = Boolean(writeState && !writeState.can_write);
  const writeBlocked = !canExecute || (isWbAccount && mpWriteBlocked);
  const ozonCarriageWriteBlocked = !canExecute || (isOzonAccount && mpWriteBlocked);
  const carriageWriteBlocked = !canExecute || ((isOzonAccount || isYandexAccount) && mpWriteBlocked);
  const workflowSelectable = WORKFLOW_SELECTABLE_GROUPS.has(current.statusGroup);
  const printSelectable = PRINT_SELECTABLE_GROUPS.has(current.statusGroup) && canDownloadLabels;
  const selectable = workflowSelectable || printSelectable;
  const shipJobRunning = msSync?.kind === 'ship_to_collected' && msSync.status === 'running';
  const labelsJobRunning = msSync?.kind === 'labels_pdf' && msSync.status === 'running';
  const productionJobRunning = msSync?.kind === 'create_production' && msSync.status === 'running';
  const taskColumns = TASK_COLUMNS.filter((column) => {
    if (column.key === 'supply_id' && current.statusGroup === 'new') return false;
    if (column.key === 'shipment_date' && !isYandexAccount) return false;
    return true;
  });
  const columns = current.kind === 'supplies'
    ? SUPPLY_COLUMNS
    : (selectable ? [{ key: '__select', label: '' }, ...taskColumns] : taskColumns);
  const visibleIds = rows.map((row) => taskExternalId(row)).filter(Boolean);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedTasks.has(id));

  return (
    <>
      <PageHeader
        title="АРМ кладовщика"
        actions={(
          <Stack direction="row" spacing={1} alignItems="center">
            {selectedAccount && (
              <Chip
                size="small"
                color={isOzonAccount ? 'warning' : (isYandexAccount ? 'info' : 'primary')}
                variant="outlined"
                label={marketplaceChipLabel(accountMarketplace)}
              />
            )}
            {current.kind === 'supplies' && isWbAccount && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => {
                  setSupplyName(`Поставка ${new Date().toLocaleDateString('ru-RU')}`);
                  setCreateOpen(true);
                }}
                disabled={writeBlocked}
              >
                Новая поставка
              </Button>
            )}
            {current.kind === 'supplies' && isOzonAccount && canExecute && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => setOzonCreateOpen(true)}
                disabled={ozonCarriageWriteBlocked || !ozonPendingMethods.length}
              >
                Создать отгрузку
              </Button>
            )}
            <Button startIcon={<RefreshIcon />} onClick={loadRows} disabled={loading}>Обновить</Button>
          </Stack>
        )}
      />

      <Stack spacing={2}>
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        {notice && (
          <Alert
            severity={noticeAlertSeverity(notice)}
            onClose={() => setNotice('')}
            sx={{ whiteSpace: 'pre-line' }}
          >
            {notice}
          </Alert>
        )}
        {!canExecute && <Alert severity="info">Доступен просмотр. Управление поставками требует отдельного права.</Alert>}
        {canExecute && writeState && !writeState.can_write && (
          <Alert severity={writeState.emergency_stop ? 'error' : 'warning'}>
            {writeState.emergency_stop
              ? `Операции ${mpName} остановлены глобальным стоп-краном.`
              : `Write-режим ${mpName} этого кабинета выключен.${
                  isOzonAccount
                    ? ' Локальная сборка и задания доступны; подтверждение carriage и печать штрихкода — нет.'
                    : isYandexAccount
                      ? ' Локальная сборка, задания, палеты и погрузка доступны; закрытие поставки, печать ШК и READY_TO_SHIP — нет.'
                      : ' Окно работает только для просмотра.'
                }`}
          </Alert>
        )}
        {writeState?.dry_run && (
          <Alert severity="info">
            Безопасный режим: изменения в {mpName} не отправляются.
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <FormControl size="small" sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0 }}>
                <InputLabel>Кабинет</InputLabel>
                <Select
                  value={accountId}
                  label="Кабинет"
                  renderValue={(value) => {
                    const account = accounts.find((item) => String(item.id) === String(value));
                    if (!account) return value || '—';
                    return (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          size="small"
                          label={marketplaceChipLabel(account.marketplace)}
                          sx={{ height: 20 }}
                        />
                        <span>{account.name}</span>
                      </Stack>
                    );
                  }}
                  onChange={(event) => {
                    const nextId = event.target.value;
                    // Сразу сбрасываем список: иначе на «Поставки» остаются WB-строки до ответа Ozon.
                    rowsLoadGenRef.current += 1;
                    setRows([]);
                    setTotal(0);
                    setOzonPendingMethods([]);
                    setOzonCreateMethodId('');
                    setSelectedTasks(new Set());
                    selectedTaskRows.current.clear();
                    setInlineSupplyCreate(false);
                    setSupplyName('');
                    setPage(0);
                    setAccountId(nextId);
                  }}
                >
                  {accounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          size="small"
                          label={marketplaceChipLabel(account.marketplace)}
                          sx={{ height: 20 }}
                        />
                        <span>{account.name}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!['supplies', 'pickingJobs', 'pallets', 'loading'].includes(current.kind) && (
                <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                  <ProductFilters
                    value={filters}
                    onChange={handleFilters}
                    defaultMp={filtersMp}
                    showMp={false}
                    showOrganization={false}
                    showExcludeMode
                    filterMd={4}
                  />
                </Box>
              )}
            </Stack>
            {!['supplies', 'pickingJobs', 'pallets', 'loading'].includes(current.kind) && (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                <TextField
                  size="small"
                  label={
                    isYandexAccount
                      ? '№ заказа, offer_id или баркод'
                      : isOzonAccount
                        ? '№ отправления, offer_id или баркод'
                        : '№ задания, RID или баркод'
                  }
                  value={query}
                  onChange={(event) => { setQuery(event.target.value); setPage(0); }}
                  sx={{ minWidth: 260, flex: 1, width: '100%' }}
                />
                {isWbAccount && (
                  <FormControl size="small" sx={{ width: { xs: '100%', md: 160 }, flexShrink: 0 }}>
                    <InputLabel id="fulfillment-cargo-type-label">Тип груза</InputLabel>
                    <Select
                      labelId="fulfillment-cargo-type-label"
                      label="Тип груза"
                      value={cargoType}
                      onChange={(event) => {
                        setCargoType(event.target.value);
                        setPage(0);
                      }}
                    >
                      <MenuItem value="">Все</MenuItem>
                      <MenuItem value="1">МГТ</MenuItem>
                      <MenuItem value="2">СГТ</MenuItem>
                    </Select>
                  </FormControl>
                )}
                <FormControl size="small" sx={{ width: { xs: '100%', md: 220 }, flexShrink: 0 }}>
                  <InputLabel id="fulfillment-warehouse-label">Склад</InputLabel>
                  <Select
                    labelId="fulfillment-warehouse-label"
                    label="Склад"
                    value={warehouseId}
                    onChange={(event) => {
                      setWarehouseId(event.target.value);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">Все</MenuItem>
                    {!isWbAccount
                      ? ozonWarehouses.map((warehouse) => (
                        <MenuItem
                          key={warehouse.warehouse_id}
                          value={String(warehouse.warehouse_id)}
                        >
                          {warehouse.warehouse_name || `Склад №${warehouse.warehouse_id}`}
                        </MenuItem>
                      ))
                      : Object.entries(warehouseNames).map(([id, name]) => (
                        <MenuItem key={id} value={id}>
                          {name || `Склад №${id}`}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                {isYandexAccount && (
                  <Box sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0 }}>
                    <DateRangeField
                      label="Дата отгрузки"
                      valueFrom={shipmentDateFrom}
                      valueTo={shipmentDateTo}
                      onChange={({ from, to }) => {
                        setShipmentDateFrom(from);
                        setShipmentDateTo(to);
                        setPage(0);
                      }}
                      size="small"
                      fullWidth
                    />
                  </Box>
                )}
                {isWbAccount && current.statusGroup !== 'new' && (
                  <TextField
                    size="small"
                    label="Поставка"
                    placeholder="WB-GI-..."
                    value={supplyId}
                    onChange={(event) => { setSupplyId(event.target.value); setPage(0); }}
                    sx={{ width: { xs: '100%', md: 220 }, flexShrink: 0 }}
                  />
                )}
              </Stack>
            )}
          </Stack>
        </Paper>

        <Paper variant="outlined">
          <Tabs value={safeTab} onChange={handleTab} variant="scrollable" scrollButtons="auto">
            {tabs.map((item) => {
              const count = item.statusGroup === 'new'
                ? tabCounts.new
                : (item.statusGroup === 'waiting_assembly'
                  ? tabCounts.waiting_assembly
                  : (item.statusGroup === 'assembly'
                    ? tabCounts.assembly
                    : (item.statusGroup === 'collected' ? tabCounts.collected : null)));
              return (
                <Tab
                  key={item.label}
                  label={(
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <span>{item.label}</span>
                      {count != null && (
                        <Box
                          component="span"
                          sx={(theme) => ({
                            minWidth: 22,
                            px: 0.75,
                            py: 0.125,
                            borderRadius: 10,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: 'text.secondary',
                            typography: 'labelSmall',
                            textAlign: 'center',
                          })}
                        >
                          {count}
                        </Box>
                      )}
                    </Stack>
                  )}
                />
              );
            })}
          </Tabs>
        </Paper>

        {current.statusGroup === 'new' && (
          <Paper variant="outlined" sx={{ px: 1.5, py: 1 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              {canExecute && (
                <>
                  <Typography variant="bodyMedium">
                    Выбрано: <strong>{selectedTasks.size}</strong>
                  </Typography>
                  {selectedTasks.size > MAX_ASSEMBLY_BATCH && (
                    <Typography variant="bodySmall" color="warning.main">
                      За один раз можно передать не более {MAX_ASSEMBLY_BATCH} заданий.
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    size="small"
                    onClick={moveSelectedToAssembly}
                    disabled={
                      !selectedTasks.size
                      || selectedTasks.size > MAX_ASSEMBLY_BATCH
                      || action === 'manual-assembly'
                    }
                  >
                    Передать в ожидание сборки
                  </Button>
                </>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={pickingListPrinting
                  ? <CircularProgress size={16} color="inherit" />
                  : <PrintIcon />}
                onClick={printPickingList}
                disabled={!accountId || pickingListPrinting}
              >
                {selectedTasks.size
                  ? `Лист подбора (${selectedTasks.size})`
                  : 'Лист подбора'}
              </Button>
              {!canExecute && selectedTasks.size > 0 && (
                <Typography variant="bodySmall" color="text.secondary">
                  Выбрано: {selectedTasks.size}
                </Typography>
              )}
            </Stack>
          </Paper>
        )}

        {current.statusGroup === 'waiting_assembly' && (
          <Paper variant="outlined" sx={{ px: 1.5, py: 1 }}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                {canExecute && (
                  <>
                    <Typography variant="bodyMedium" sx={{ mr: 0.5 }}>
                      Выбрано: <strong>{selectedTasks.size}</strong>
                    </Typography>
                    {selectedTasks.size > MAX_ASSEMBLY_BATCH && (
                      <Typography variant="bodySmall" color="warning.main">
                        За один раз можно обработать не более {MAX_ASSEMBLY_BATCH} заказов.
                      </Typography>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={action === 'manual-new'
                        ? <CircularProgress size={16} color="inherit" />
                        : <UndoIcon />}
                      onClick={moveSelectedToNew}
                      disabled={
                        !selectedTasks.size
                        || selectedTasks.size > MAX_ASSEMBLY_BATCH
                        || Boolean(action)
                      }
                    >
                      {action === 'manual-new' ? 'Возврат…' : 'В новые'}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={openPickingCreate}
                      disabled={!selectedTasks.size || action === 'create-picking-job' || action === 'create-production' || productionJobRunning}
                    >
                      Создать задание
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={action === 'create-production' || productionJobRunning
                        ? <CircularProgress size={16} color="inherit" />
                        : <ProductionIcon />}
                      onClick={createProductionFromSelected}
                      disabled={
                        !selectedTasks.size
                        || selectedTasks.size > MAX_ASSEMBLY_BATCH
                        || Boolean(action)
                        || productionJobRunning
                      }
                    >
                      {action === 'create-production' || productionJobRunning ? 'Создание…' : 'Создать производство'}
                    </Button>
                  </>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={pickingListPrinting
                    ? <CircularProgress size={16} color="inherit" />
                    : <PrintIcon />}
                  onClick={printPickingList}
                  disabled={!accountId || pickingListPrinting}
                >
                  {selectedTasks.size
                    ? `Лист подбора (${selectedTasks.size})`
                    : 'Лист подбора'}
                </Button>
                {!canExecute && selectedTasks.size > 0 && (
                  <Typography variant="bodySmall" color="text.secondary">
                    Выбрано: {selectedTasks.size}
                  </Typography>
                )}
              </Stack>
              {canExecute && (isOzonAccount || isYandexAccount) && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={shipSelectedToCollected}
                  disabled={
                    !selectedTasks.size
                    || selectedTasks.size > MAX_ASSEMBLY_BATCH
                    || !canExecute
                    || mpWriteBlocked
                    || shipJobRunning
                  }
                >
                  {shipJobRunning
                    ? 'Передача…'
                    : 'В сборку (к отгрузке)'}
                </Button>
              )}
              {canExecute && isWbAccount && (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ sm: 'center' }}
                >
                  {inlineSupplyCreate ? (
                    <>
                      <TextField
                        size="small"
                        label="Название поставки"
                        value={supplyName}
                        onChange={(event) => setSupplyName(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' && supplyName.trim()) createSupply();
                        }}
                        disabled={writeBlocked || action === 'create'}
                        sx={{ flex: 1, minWidth: { xs: '100%', sm: 220 } }}
                        inputProps={{ maxLength: 128 }}
                        autoFocus
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={createSupply}
                        disabled={!supplyName.trim() || writeBlocked || action === 'create'}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {action === 'create' ? 'Создание…' : 'Создать'}
                      </Button>
                      <Button size="small" onClick={cancelInlineSupplyCreate} disabled={action === 'create'}>
                        Отмена
                      </Button>
                    </>
                  ) : (
                    <>
                      <FormControl size="small" sx={{ flex: 1, minWidth: { xs: '100%', sm: 220 } }}>
                        <InputLabel>Поставка WB</InputLabel>
                        <Select
                          value={targetSupplyId}
                          label="Поставка WB"
                          onChange={(event) => setTargetSupplyId(event.target.value)}
                          disabled={writeBlocked || !openSupplies.length}
                        >
                          {openSupplies.map((supply) => {
                            const supplyType = supplyB2bType(supply);
                            const conflict = supplyB2bConflict(supply, selectedB2b.type);
                            return (
                              <MenuItem
                                key={supply.wb_supply_id}
                                value={supply.wb_supply_id}
                                disabled={conflict}
                              >
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                  <span>{supply.name?.trim() || supply.wb_supply_id}</span>
                                  <B2bChip kind="supply" show={supplyType === true} sx={{ height: 20 }} />
                                  {conflict && (
                                    <Typography variant="caption" color="text.secondary">
                                      {supplyType ? 'Поставка для юрлиц' : 'Обычная поставка'}
                                    </Typography>
                                  )}
                                </Stack>
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={startInlineSupplyCreate}
                        disabled={writeBlocked}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        Новая
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={addSelectedToSupply}
                        disabled={
                          !selectedTasks.size
                          || selectedTasks.size > MAX_ASSEMBLY_BATCH
                          || !targetSupplyId
                          || writeBlocked
                          || action === 'add-to-supply'
                          || selectedB2b.mixed
                          || targetSupplyB2bConflict
                        }
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {action === 'add-to-supply' ? 'Добавление…' : 'Добавить выбранные'}
                      </Button>
                    </>
                  )}
                </Stack>
              )}
              {canExecute && isWbAccount && selectedB2b.mixed && (
                <Alert severity="warning" sx={{ py: 0 }}>{B2B_MIXED_SELECTION_HINT}</Alert>
              )}
              {canExecute && isWbAccount && !selectedB2b.mixed && targetSupplyB2bConflict && (
                <Alert severity="warning" sx={{ py: 0 }}>
                  {selectedB2b.type
                    ? 'Выбраны заказы юрлиц — выберите поставку для юрлиц или создайте новую'
                    : 'Выбраны обычные заказы — поставка для юрлиц не подходит, выберите другую или создайте новую'}
                </Alert>
              )}
            </Stack>
          </Paper>
        )}

        {printSelectable && (
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <Typography variant="bodyMedium" sx={{ minWidth: 120 }}>
                Выбрано: <strong>{selectedTasks.size}</strong>
              </Typography>
              {selectedTasks.size > MAX_STICKER_BATCH && (
                <Typography variant="bodySmall" color="error.main">
                  За один PDF можно скачать не более {MAX_STICKER_BATCH} этикеток.
                </Typography>
              )}
              <Button
                variant="contained"
                startIcon={printAction === 'batch-stickers' ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                onClick={printSelectedStickers}
                disabled={!selectedTasks.size || selectedTasks.size > MAX_STICKER_BATCH || Boolean(printAction) || labelsJobRunning}
                sx={{ minWidth: 220 }}
              >
                Скачать PDF
              </Button>
            </Stack>
          </Paper>
        )}

        {msSync && (
          <Paper variant="outlined" sx={{ px: 1.5, py: 1 }}>
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                <Typography variant="bodySmall" color="text.secondary">
                  {msSyncKindLabel(msSync.kind).progress}
                  {' '}
                  <strong>
                    {Number(msSync.done) || 0}
                    {' / '}
                    {Number(msSync.total) || 0}
                  </strong>
                  {Number(msSync.failed) > 0 ? ` · ошибок: ${msSync.failed}` : ''}
                  {msSync.message ? ` · ${msSync.message}` : ''}
                </Typography>
                {(msSync.status === 'completed' || msSync.status === 'failed') && (
                  <Chip
                    size="small"
                    color={msSync.status === 'failed' || Number(msSync.failed) > 0 ? 'warning' : 'success'}
                    label={msSync.status === 'failed'
                      ? 'Сбой'
                      : (Number(msSync.failed) > 0 ? 'Готово с ошибками' : 'Готово')}
                  />
                )}
              </Stack>
              <LinearProgress
                variant={msSync.status === 'running' && !(Number(msSync.done) > 0 && Number(msSync.total) > 0)
                  ? 'indeterminate'
                  : 'determinate'}
                value={Number(msSync.total) > 0
                  ? Math.min(100, (100 * (Number(msSync.done) || 0)) / Number(msSync.total))
                  : 0}
              />
            </Stack>
          </Paper>
        )}

        {current.kind === 'pickingJobs' || current.kind === 'pallets' || current.kind === 'loading' ? (
          current.kind === 'pickingJobs' ? (
            <PickingJobsPanel
              accountId={accountId}
              marketplace={accountMarketplace}
              canManage={canExecute}
              refreshKey={pickingRefreshKey}
              onChanged={loadTabCounts}
            />
          ) : current.kind === 'pallets' ? (
            <PalletsPanel
              accountId={accountId}
              marketplace={accountMarketplace}
              canManage={canExecute}
              warehouseNames={warehouseNameMap}
            />
          ) : (
            <LoadingSessionsPanel
              accountId={accountId}
              marketplace={accountMarketplace}
              canConfirm={canExecute}
              writeStateDryRun={Boolean(writeState?.dry_run)}
            />
          )
        ) : (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 330px)' }}>
            <Table
              size="small"
              stickyHeader
              sx={{
                minWidth: current.kind === 'supplies' ? 760 : 990,
                '& .MuiTableCell-body': { py: 0.625 },
                '& .MuiChip-root': { height: 22 },
                '& .MuiChip-label': { px: 0.875 },
              }}
            >
              <TableHead>
                <TableRow>
                  {(current.kind === 'supplies' && isCarriageAccount ? OZON_SUPPLY_COLUMNS : columns).map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.align || 'left'}
                      sx={column.desktopOnly ? { display: { xs: 'none', md: 'table-cell' } } : undefined}
                    >
                      {column.key === '__select' ? (
                        <Checkbox
                          size="small"
                          checked={allVisibleSelected}
                          indeterminate={visibleIds.some((id) => selectedTasks.has(id)) && !allVisibleSelected}
                          onChange={(event) => {
                            setSelectedTasks((old) => {
                              const next = new Set(old);
                              rows.forEach((row) => {
                                const id = taskExternalId(row);
                                if (!id) return;
                                if (event.target.checked) {
                                  next.add(id);
                                  selectedTaskRows.current.set(id, row);
                                } else {
                                  next.delete(id);
                                  selectedTaskRows.current.delete(id);
                                }
                              });
                              return next;
                            });
                          }}
                          inputProps={{ 'aria-label': 'Выбрать все задания на странице' }}
                        />
                      ) : column.sortable ? (
                        <TableSortLabel
                          active={sort.key === column.key}
                          direction={sort.key === column.key ? sort.dir : 'asc'}
                          onClick={() => handleSort(column.key)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              {current.kind === 'supplies' ? (
                isCarriageAccount ? (
                  <OzonCarriagesBody
                    carriages={rows}
                    accountId={accountId}
                    marketplace={accountMarketplace}
                    emptyLabel={isYandexAccount
                      ? 'Поставок пока нет. Они появятся после sync first-mile из ЛК Yandex.'
                      : undefined}
                    action={action}
                    writeBlocked={carriageWriteBlocked}
                    onApprove={approveOzonCarriage}
                    onPrintBarcode={printOzonCarriageBarcode}
                    onDownloadAct={downloadYandexShipmentAct}
                    writeStateDryRun={Boolean(writeState?.dry_run)}
                    canPrint={canUsePrintQueue && Boolean(printer.selectedPrinter?.printer_uid)}
                    printBlockedReason={
                      !canUsePrintQueue
                        ? 'Нет права на очередь печати'
                        : (!printer.selectedPrinter?.printer_uid
                          ? PRINTER_NOT_SELECTED_MSG
                          : (carriageWriteBlocked ? `Write ${mpName} выключен` : ''))
                    }
                    showPrintSticker={canDownloadLabels}
                    canPrintSticker={!printAction}
                    onPrintSticker={printTaskLabel}
                    printAction={printAction}
                  />
                ) : (
                  <SuppliesBody
                    supplies={rows}
                    accountId={accountId}
                    warehouseNames={warehouseNames}
                    action={action}
                    writeBlocked={writeBlocked}
                    onDeliver={openDeliver}
                    onDelete={deleteSupply}
                    onPrintQr={(supply) => enqueuePrint('wb_supply_qr', supply.wb_supply_id, `qr:${supply.wb_supply_id}`)}
                    showPrintQr={canUsePrintQueue}
                    canPrintQr={!printAction}
                    onPrintSticker={(task) => downloadLabelsPdf(
                      task.wb_order_id,
                      'labels',
                      `sticker:${task.wb_order_id}`,
                    )}
                    showPrintSticker={canDownloadLabels}
                    canPrintSticker={!printAction}
                    printAction={printAction}
                  />
                )
              ) : (
                <TasksBody
                  rows={rows}
                  now={minuteNow}
                  showAge={current.statusGroup !== 'canceled'}
                  showSupplyColumn={current.statusGroup !== 'new'}
                  showShipmentDate={isYandexAccount}
                  columnCount={columns.length}
                  warehouseNames={warehouseNameMap}
                  onJournal={openJournal}
                  onProjectToMs={projectToMs}
                  canProjectToMs={
                    canExecute
                    && Boolean(writeState?.ms_projection_enabled || writeState?.ms_projection)
                    && MS_LINKABLE_GROUPS.has(current.statusGroup)
                  }
                  action={action}
                  selectable={selectable}
                  selectedTasks={selectedTasks}
                  showPrintSticker={canDownloadLabels}
                  canPrintSticker={!printAction}
                  onPrintSticker={printTaskLabel}
                  printAction={printAction}
                  onToggleSelected={(row) => {
                    setSelectedTasks((old) => {
                      const next = new Set(old);
                      const key = taskExternalId(row);
                      if (!key) return old;
                      if (next.has(key)) {
                        next.delete(key);
                        selectedTaskRows.current.delete(key);
                      } else {
                        next.add(key);
                        selectedTaskRows.current.set(key, row);
                      }
                      return next;
                    });
                  }}
                />
              )}
            </Table>
          </TableContainer>
          {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={28} /></Box>}
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_event, value) => setPage(value)}
            onRowsPerPageChange={(event) => {
              const value = Number(event.target.value);
              localStorage.setItem(PAGE_SIZE_KEY, String(value));
              setRowsPerPage(value);
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 200, MAX_TASKS_PAGE_SIZE]}
            labelRowsPerPage="На странице:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
          />
        </Paper>
        )}
      </Stack>

      <Dialog open={Boolean(deliverTarget)} onClose={closeDeliver} fullWidth maxWidth="sm">
        <DialogTitle>Передать поставку в доставку</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {deliverTarget?.wb_supply_id
              ? `Поставка ${deliverTarget.wb_supply_id}. С 1 октября 2026 WB требует пункт, дату и способ отгрузки до закрытия поставки.`
              : 'С 1 октября 2026 WB требует пункт, дату и способ отгрузки до закрытия поставки.'}
          </Typography>
          <WbSupplyShippingFields
            form={shippingForm}
            loading={shippingLoading}
            error={shippingError}
            onChange={setShippingForm}
            onCityChange={(city) => {
              if (!deliverTarget?.wb_supply_id) return;
              setShippingForm((prev) => (prev ? { ...prev, city } : prev));
              loadDeliverOptions(deliverTarget.wb_supply_id, city, shippingForm);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeliver} disabled={Boolean(action)}>Отмена</Button>
          <Button
            variant="contained"
            disabled={Boolean(action) || shippingLoading || !shippingFormReady(shippingForm)}
            onClick={confirmDeliver}
            startIcon={action ? <CircularProgress size={16} color="inherit" /> : <DeliverIcon />}
          >
            Передать в доставку
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pickingCreateOpen} onClose={() => setPickingCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Создать задание на подбор</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              label="Название задания"
              value={pickingName}
              onChange={(event) => setPickingName(event.target.value)}
              inputProps={{ maxLength: 160 }}
            />
            <FormControl fullWidth>
              <InputLabel>Сборщик</InputLabel>
              <Select
                value={pickingAssignee}
                label="Сборщик"
                onChange={(event) => setPickingAssignee(event.target.value)}
              >
                {pickingAssignees.map((assignee) => (
                  <MenuItem key={assignee.id} value={assignee.id}>{assignee.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={(
                <Switch
                  checked={pickingLabelingMode === LABELING_MODES.DURING_PICKING}
                  onChange={(event) => {
                    const next = event.target.checked ? LABELING_MODES.DURING_PICKING : LABELING_MODES.AFTER_TRANSFER;
                    setPickingLabelingMode(next);
                    try {
                      localStorage.setItem(PICKING_LABELING_MODE_KEY, next);
                    } catch {
                      /* приватный режим браузера — просто не запоминаем */
                    }
                  }}
                />
              )}
              label="Проклейка во время сборки (на палеты — на складе отгрузки)"
            />
            <Typography variant="bodySmall" color="text.secondary">
              В задание войдут выбранные заказы: {selectedTasks.size}.
              Это внутреннее действие Packer — статус на маркетплейсе не меняется.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickingCreateOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={createPickingJob}
            disabled={
              !pickingName.trim()
              || action === 'create-picking-job'
              || (isWbAccount && !pickingAssignee)
            }
          >
            Создать
          </Button>
        </DialogActions>
      </Dialog>


      <Dialog
        open={productionDialog.open}
        onClose={() => action !== 'create-production' && closeProductionDialog()}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Выберите техкарты</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="bodySmall" color="text.secondary">
              У этих товаров несколько техкарт. Выберите нужную — затем создадим одно производственное задание на все выбранные заказы.
              {productionDialog.restCount > 0
                ? ` Ещё ${productionDialog.restCount} товар(ов) с одной техкартой войдут в тот же документ.`
                : ''}
            </Typography>
            {productionDialog.items.map((item) => {
              const cards = Array.isArray(item.techcards) ? item.techcards : [];
              const title = item.ms_product_name || item.article || item.ms_product_id;
              const subtitle = [
                item.article && item.article !== title ? item.article : null,
                item.nm_id ? `nmID ${item.nm_id}` : null,
                item.quantity ? `${item.quantity} шт.` : null,
              ].filter(Boolean).join(' · ');
              return (
                <Paper key={item.ms_product_id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={1}>
                    <Typography variant="bodyMedium">{title}</Typography>
                    {subtitle && (
                      <Typography variant="bodySmall" color="text.secondary">{subtitle}</Typography>
                    )}
                    <FormControl fullWidth size="small">
                      <InputLabel>Техкарта</InputLabel>
                      <Select
                        value={productionDialog.selections[item.ms_product_id] || ''}
                        label="Техкарта"
                        onChange={(event) => setProductionDialog((prev) => ({
                          ...prev,
                          selections: {
                            ...prev.selections,
                            [item.ms_product_id]: event.target.value,
                          },
                        }))}
                      >
                        {cards.map((card) => (
                          <MenuItem key={card.id} value={card.id}>{techCardLabel(card)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeProductionDialog} disabled={action === 'create-production' || productionJobRunning}>Отмена</Button>
          <Button
            variant="contained"
            onClick={confirmProductionDialog}
            disabled={
              action === 'create-production'
              || productionJobRunning
              || productionDialog.items.some((item) => !String(productionDialog.selections[item.ms_product_id] || '').trim())
            }
          >
            {action === 'create-production' || productionJobRunning ? 'Создание…' : 'Создать производство'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Создать поставку WB</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Название поставки"
            value={supplyName}
            onChange={(event) => setSupplyName(event.target.value)}
            sx={{ mt: 1 }}
            inputProps={{ maxLength: 128 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={createSupply} disabled={!supplyName.trim() || action === 'create'}>
            Создать
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={ozonCreateOpen}
        onClose={() => action !== 'create-ozon-carriage' && setOzonCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Создать отгрузку Ozon</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="bodySmall" color="text.secondary">
              Создаётся carriage по способу доставки из заказов в «Сборка» (awaiting_deliver),
              затем сразу закрывается (approve). Обычно отгрузки появляются из ЛК Ozon после sync.
            </Typography>
            <FormControl fullWidth disabled={ozonCarriageWriteBlocked}>
              <InputLabel id="ozon-create-method-label">Способ доставки</InputLabel>
              <Select
                labelId="ozon-create-method-label"
                label="Способ доставки"
                value={ozonCreateMethodId}
                onChange={(event) => setOzonCreateMethodId(event.target.value)}
              >
                {ozonPendingMethods.length === 0 ? (
                  <MenuItem value="" disabled>Нет ожидающих awaiting_deliver</MenuItem>
                ) : ozonPendingMethods.map((method) => (
                  <MenuItem
                    key={method.delivery_method_id}
                    value={String(method.delivery_method_id)}
                  >
                    {method.delivery_method_name || method.delivery_method_id}
                    {' · '}
                    {method.postings_count}
                    {' шт'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOzonCreateOpen(false)}
            disabled={action === 'create-ozon-carriage'}
          >
            Отмена
          </Button>
          <Tooltip title={ozonCarriageWriteBlocked ? 'Write Ozon выключен' : ''}>
            <span>
              <Button
                variant="contained"
                onClick={createAndCloseOzonCarriage}
                disabled={
                  Boolean(action)
                  || ozonCarriageWriteBlocked
                  || !ozonCreateMethodId
                  || !ozonPendingMethods.length
                }
                startIcon={action === 'create-ozon-carriage'
                  ? <CircularProgress size={14} color="inherit" />
                  : <AddIcon />}
              >
                Создать и закрыть
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      <Dialog
        open={journal.open}
        onClose={() => setJournal({ open: false, task: null, events: [], loading: false })}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Журнал задания № {journal.task?.wb_order_id}
          <B2bChip
            show={taskMarketplace(journal.task || {}) === 'wb' && isB2bTask(journal.task)}
            sx={{ ml: 1, verticalAlign: 'middle' }}
          />
        </DialogTitle>
        <DialogContent dividers>
          {journal.loading ? <CircularProgress /> : journal.events.length ? (
            <Stack spacing={1}>
              {journal.events.map((event) => (
                <Paper key={event.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="body2">{formatDate(event.created_at)} · {event.source || 'sync'}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {event.field || 'status'}: {String(event.from_value ?? event.from ?? '—')} → {String(event.to_value ?? event.to ?? '—')}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          ) : <Typography color="text.secondary">Событий пока нет.</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJournal({ open: false, task: null, events: [], loading: false })}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmText={confirm?.confirmText}
        onClose={() => setConfirm(null)}
        onConfirm={confirm?.onConfirm}
      />
    </>
  );
}

function TasksBody({
  rows,
  now,
  showAge,
  showSupplyColumn = true,
  showShipmentDate = false,
  columnCount,
  warehouseNames,
  onJournal,
  onProjectToMs,
  canProjectToMs,
  action,
  selectable,
  selectedTasks,
  showPrintSticker,
  canPrintSticker,
  onPrintSticker,
  printAction,
  onToggleSelected,
}) {
  return (
    <TableBody>
      {rows.map((row) => {
        const status = normalizedStatus(row);
        const externalId = taskExternalId(row);
        const marketplace = taskMarketplace(row);
        const warehouse = warehousePresentation(
          row,
          warehouseNames[String(row.warehouse_id)] || row.warehouse_label || '',
        );
        const canMs = canProjectToMs && (marketplace === 'wb' || marketplace === 'ozon' || marketplace === 'yandex');
        const sourceId = taskSourceId(row) || externalId;
        const canPrint = showPrintSticker && taskCanPrintLabel(row);
        return (
          <TableRow key={`${marketplace}:${externalId || row.id}`} hover>
            {selectable && (
              <TableCell padding="checkbox" sx={{ verticalAlign: 'top' }}>
                <Checkbox
                  size="small"
                  checked={selectedTasks.has(String(externalId))}
                  onChange={() => onToggleSelected(row)}
                  inputProps={{ 'aria-label': `Выбрать задание ${externalId}` }}
                />
              </TableCell>
            )}
            <TableCell sx={{ minWidth: 190, verticalAlign: 'top' }}>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
                <Chip size="small" label={marketplaceChipLabel(marketplace)} sx={{ height: 20 }} />
                {row.composition_alert ? (
                  <Chip size="small" color="warning" label="Состав" sx={{ height: 20 }} />
                ) : null}
                <B2bChip show={marketplace === 'wb' && isB2bTask(row)} sx={{ height: 20 }} />
              </Stack>
              <Typography variant="titleMedium" fontWeight={700}>{externalId}</Typography>
              <Typography variant="bodySmall" color="text.secondary">{formatDate(row.created_at_mp)}</Typography>
              <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                {showAge && (
                  <Chip
                    size="small"
                    label={formatOrderAge(row.created_at_mp, now)}
                    sx={ageChipSx(orderAgeTone(row.created_at_mp, now))}
                  />
                )}
                {marketplace === 'wb' && (
                  <Chip size="small" label={warehouse.cargo.label} sx={cargoChipSx} />
                )}
                {row.picking_job?.code && (
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={row.picking_job.code}
                  />
                )}
              </Stack>
            </TableCell>
            <TableCell sx={{ minWidth: 300, verticalAlign: 'top' }}>
              <TaskProductCell row={row} />
            </TableCell>
            <TableCell sx={{ verticalAlign: 'middle' }}>
              <Tooltip title={taskStatusTooltip(row, marketplace)}>
                <Chip
                  size="small"
                  label={status.label}
                  color={status.code === 'shipment_error' ? 'error' : undefined}
                  sx={statusChipSx(status.code)}
                />
              </Tooltip>
            </TableCell>
            <TableCell sx={{ verticalAlign: 'middle' }}>
              <Typography variant="bodyMedium" fontWeight={700}>{warehouse.warehouseLabel}</Typography>
              {marketplace === 'wb' && (
                <Chip
                  size="small"
                  label={warehouse.cargo.label}
                  sx={{ ...cargoChipSx, mt: 0.375 }}
                />
              )}
            </TableCell>
            {showShipmentDate && (
              <TableCell sx={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                {formatDateOnly(row.shipment_date)}
              </TableCell>
            )}
            {showSupplyColumn && (
              <TableCell sx={{ verticalAlign: 'top' }}>{row.supply_id || '—'}</TableCell>
            )}
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, verticalAlign: 'top' }}>
              {money(row.sale_price ?? row.final_price ?? row.price)}
            </TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, verticalAlign: 'middle' }}>
              {row.ms_id ? (
                <Tooltip title="Открыть заказ в МойСклад">
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    href={MS_ORDER_HREF(row.ms_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    endIcon={<OpenInNewIcon fontSize="small" />}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                  >
                    Открыть
                  </Button>
                </Tooltip>
              ) : canMs ? (
                <Tooltip title="Найти в МойСклад и связать, либо создать заказ">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        action === `ms:${externalId}`
                          ? <CircularProgress size={14} color="inherit" />
                          : <LinkIcon fontSize="small" />
                      }
                      onClick={() => onProjectToMs(row)}
                      disabled={Boolean(action)}
                      sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                    >
                      Связать
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Typography variant="caption" color="text.secondary">Не связан</Typography>
              )}
            </TableCell>
            <TableCell align="right" sx={{ verticalAlign: 'top' }}>
              {canPrint && (
                <Tooltip title="Скачать PDF этикетки">
                  <span>
                    <IconButton
                      color="primary"
                      onClick={() => onPrintSticker(row)}
                      disabled={!canPrintSticker || Boolean(printAction)}
                    >
                      {printAction === `sticker:${sourceId}`
                        ? <CircularProgress size={20} />
                        : <DownloadIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Открыть журнал">
                <IconButton onClick={() => onJournal(row)}><HistoryIcon /></IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        );
      })}
      {!rows.length && (
        <TableRow>
          <TableCell
            colSpan={columnCount || (TASK_COLUMNS.length + (selectable ? 1 : 0))}
            align="center"
            sx={{ py: 6 }}
          >
            Нет заданий по выбранным фильтрам
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  );
}

function SuppliesBody({
  supplies,
  accountId,
  warehouseNames,
  action,
  writeBlocked,
  onDeliver,
  onDelete,
  onPrintQr,
  showPrintQr,
  canPrintQr,
  onPrintSticker,
  showPrintSticker,
  canPrintSticker,
  printAction,
}) {
  const [dialog, setDialog] = useState({
    open: false,
    supply: null,
    loading: false,
    items: [],
    error: '',
  });

  const openSupply = async (supply) => {
    const id = supply.wb_supply_id;
    setDialog({ open: true, supply, loading: true, items: [], error: '' });
    try {
      const { data } = await axios.get(`/fulfillment/supplies/${encodeURIComponent(id)}`, {
        params: { account_id: accountId },
      });
      setDialog({
        open: true,
        supply: data?.supply || supply,
        loading: false,
        items: data?.tasks || [],
        error: '',
      });
    } catch (requestError) {
      setDialog({
        open: true,
        supply,
        loading: false,
        items: [],
        error: apiError(requestError),
      });
    }
  };

  return (
    <>
      <TableBody>
        {supplies.map((supply) => {
          const status = supply.normalized_status || {
            code: supply.done ? 'awaiting_acceptance' : 'forming',
            label: supply.done ? 'Передана, ожидает приёмки' : 'Формируется',
          };
          const warehouseId = supply.warehouse_id == null ? '' : String(supply.warehouse_id);
          const warehouseName = warehouseNames[warehouseId]
            || supply.warehouse_name
            || (warehouseId ? `Склад №${warehouseId}` : 'Склад не определён');
          return (
            <TableRow hover>
              <TableCell sx={{ width: 44, pr: 0 }}>
                <Tooltip title="Открыть товары поставки">
                  <IconButton
                    size="small"
                    aria-label="Открыть товары поставки"
                    onClick={() => openSupply(supply)}
                  >
                    <ViewIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Typography variant="titleMedium" fontWeight={700}>{supply.name || supply.wb_supply_id}</Typography>
                <Typography variant="caption" color="text.secondary" component="p">{supply.wb_supply_id}</Typography>
                <B2bChip kind="supply" show={supplyB2bType(supply) === true} sx={{ mt: 0.375 }} />
              </TableCell>
              <TableCell>
                <Typography variant="bodyMedium" fontWeight={700}>
                  {Number(supply.tasks_total) || 0} товаров
                </Typography>
                <Typography variant="caption" color="text.secondary" component="p">
                  Принято WB: {Number(supply.tasks_accepted) || 0}
                </Typography>
                {Number(supply.tasks_reshipment) > 0 && (
                  <Typography variant="caption" color="warning.main" component="p">
                    В перепоставку: {Number(supply.tasks_reshipment)}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="bodyMedium" fontWeight={700}>{warehouseName}</Typography>
                <Chip size="small" label={warehousePresentation({ cargo_type: supply.cargo_type }).cargo.label} sx={{ ...cargoChipSx, mt: 0.375 }} />
              </TableCell>
              <TableCell>
                <Chip size="small" label={status.label} sx={supplyStatusChipSx(status.code)} />
                {supply.reject_reason && (
                  <Typography variant="caption" color="error.main" component="p" sx={{ mt: 0.5 }}>
                    {supply.reject_reason}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="caption" color="text.secondary" component="p">
                  Создана: {formatDate(supply.created_at_mp)}
                </Typography>
                {supply.closed_at && (
                  <Typography variant="caption" color="text.secondary" component="p">
                    Передана: {formatDate(supply.closed_at)}
                  </Typography>
                )}
                {supply.scan_dt && (
                  <Typography variant="caption" color="success.main" component="p">
                    Принята: {formatDate(supply.scan_dt)}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">
                {supply.done && showPrintQr && (
                  <Tooltip title="Печать QR поставки">
                    <span>
                      <IconButton
                        color="primary"
                        disabled={!canPrintQr || Boolean(printAction)}
                        onClick={() => onPrintQr(supply)}
                      >
                        {printAction === `qr:${supply.wb_supply_id}`
                          ? <CircularProgress size={20} />
                          : <QrCodeIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {!supply.done && (
                  <>
                    <Tooltip title="Передать в доставку">
                      <span>
                        <IconButton
                          color="primary"
                          disabled={writeBlocked || Boolean(action)}
                          onClick={() => onDeliver(supply)}
                        >
                          <DeliverIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Удалить пустую поставку">
                      <span>
                        <IconButton
                          color="error"
                          disabled={writeBlocked || Boolean(action)}
                          onClick={() => onDelete(supply.wb_supply_id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}
              </TableCell>
            </TableRow>
          );
        })}
        {!supplies.length && (
          <TableRow><TableCell colSpan={SUPPLY_COLUMNS.length} align="center" sx={{ py: 6 }}>Поставок пока нет</TableCell></TableRow>
        )}
      </TableBody>

      <Dialog
        open={dialog.open}
        onClose={() => setDialog((old) => ({ ...old, open: false }))}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          Товары поставки {dialog.supply?.name || dialog.supply?.wb_supply_id || ''}
          <B2bChip kind="supply" show={supplyB2bType(dialog.supply) === true} sx={{ ml: 1, verticalAlign: 'middle' }} />
          {dialog.supply?.name && (
            <Typography variant="bodySmall" color="text.secondary" component="p">
              {dialog.supply.wb_supply_id}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {dialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={32} /></Box>
          ) : dialog.error ? (
            <Alert severity="error">{dialog.error}</Alert>
          ) : dialog.items.length ? (
            <TableContainer sx={{ maxHeight: '70vh' }}>
              <Table size="small" stickyHeader aria-label={`Товары поставки ${dialog.supply?.wb_supply_id || ''}`}>
                <TableHead>
                  <TableRow>
                    <TableCell>Задание / дата</TableCell>
                    <TableCell>Товар</TableCell>
                    <TableCell>Статус</TableCell>
                    {showPrintSticker && <TableCell align="right">Печать</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dialog.items.map((task) => {
                    const taskStatus = normalizedStatus(task);
                    return (
                      <TableRow key={task.wb_order_id} hover>
                        <TableCell sx={{ width: 190 }}>
                          <Typography variant="bodyMedium" fontWeight={700}>№ {task.wb_order_id}</Typography>
                          <Typography variant="caption" color="text.secondary" component="p">{formatDate(task.created_at_mp)}</Typography>
                          <B2bChip show={isB2bTask(task)} sx={{ mt: 0.375 }} />
                        </TableCell>
                        <TableCell>
                          <TaskProductCell row={task} />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={taskStatusTooltip(task, taskMarketplace(task))}>
                            <Chip
                              size="small"
                              label={taskStatus.label}
                              color={taskStatus.code === 'shipment_error' ? 'error' : undefined}
                              sx={statusChipSx(taskStatus.code)}
                            />
                          </Tooltip>
                        </TableCell>
                        {showPrintSticker && (
                          <TableCell align="right">
                            {taskCanPrintLabel(task) && (
                            <Tooltip title="Скачать PDF этикетки">
                              <span>
                                <IconButton
                                  color="primary"
                                  onClick={() => onPrintSticker(task)}
                                  disabled={!canPrintSticker || Boolean(printAction)}
                                >
                                  {printAction === `sticker:${task.wb_order_id}`
                                    ? <CircularProgress size={20} />
                                    : <DownloadIcon />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              В поставке пока нет товаров
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog((old) => ({ ...old, open: false }))}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function OzonCarriagesBody({
  carriages,
  accountId,
  marketplace = 'ozon',
  readOnly = false,
  emptyLabel,
  action,
  writeBlocked,
  onApprove,
  onPrintBarcode,
  onDownloadAct,
  writeStateDryRun = false,
  canPrint,
  printBlockedReason,
  showPrintSticker = false,
  canPrintSticker = true,
  onPrintSticker,
  printAction = '',
}) {
  const [dialog, setDialog] = useState({
    open: false,
    carriage: null,
    loading: false,
    items: [],
    error: '',
  });

  const openCarriage = async (carriage) => {
    const id = carriage.carriage_id ?? carriage.shipment_id ?? carriage.external_id;
    setDialog({ open: true, carriage, loading: true, items: [], error: '' });
    try {
      const detailUrl = marketplace === 'yandex'
        ? `/fulfillment/yandex/shipments/${encodeURIComponent(id)}`
        : `/fulfillment/ozon/carriages/${encodeURIComponent(id)}`;
      const { data } = await axios.get(detailUrl, {
        params: { account_id: accountId },
      });
      setDialog({
        open: true,
        carriage: data?.carriage || data?.shipment || data?.supply || carriage,
        loading: false,
        items: data?.tasks || data?.postings || [],
        error: '',
      });
    } catch (requestError) {
      setDialog({
        open: true,
        carriage,
        loading: false,
        items: [],
        error: apiError(requestError),
      });
    }
  };

  return (
    <>
      <TableBody>
        {carriages.map((carriage) => {
          const carriageId = carriage.carriage_id ?? carriage.shipment_id ?? carriage.external_id;
          const status = carriage.normalized_status || {
            code: carriage.done ? 'awaiting_acceptance' : 'forming',
            label: carriage.status_label || (carriage.done ? 'Подтверждена' : 'Формируется'),
          };
          const methodLabel = carriage.delivery_method_name
            || (carriage.delivery_method_id != null ? `Метод ${carriage.delivery_method_id}` : 'Способ не указан');
          const approveBusy = action === `approve-carriage:${carriageId}`;
          const printBusy = action === `print-carriage:${carriageId}`;
          const downloadBusy = action === `download-act:${carriageId}`;
          const canDownloadAct = Boolean(carriage.can_download_act);
          const actBlockedReason = canDownloadAct
            ? ''
            : (writeStateDryRun
              ? 'В режиме dry-run официальный акт недоступен'
              : 'Сначала закройте поставку');
          return (
            <TableRow key={String(carriageId)} hover>
              <TableCell sx={{ width: 44, pr: 0 }}>
                <Tooltip title="Открыть состав отгрузки">
                  <IconButton
                    size="small"
                    aria-label="Открыть состав отгрузки"
                    onClick={() => openCarriage(carriage)}
                  >
                    <ViewIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Typography variant="titleMedium" fontWeight={700}>
                  {carriage.name || `Отгрузка ${carriageId}`}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="p">
                  {carriageId}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="bodyMedium" fontWeight={700}>
                  {Number(carriage.tasks_total) || 0} заказов
                </Typography>
                {carriage.act_ready && (
                  <Typography variant="caption" color="success.main" component="p">
                    Акт готов
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip size="small" label={methodLabel} sx={{ ...cargoChipSx, mt: 0.125 }} />
              </TableCell>
              <TableCell>
                <Chip size="small" label={status.label} sx={supplyStatusChipSx(status.code)} />
              </TableCell>
              <TableCell>
                <Typography variant="caption" color="text.secondary" component="p">
                  Создана: {formatDate(carriage.created_at_mp || carriage.created_at)}
                </Typography>
                {carriage.closed_at && (
                  <Typography variant="caption" color="text.secondary" component="p">
                    Закрыта: {formatDate(carriage.closed_at)}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                  {!readOnly && carriage.can_close !== false && !carriage.done && (
                    <Tooltip title={writeBlocked ? `Write ${marketplace === 'yandex' ? 'Yandex' : 'Ozon'} выключен` : 'Закрыть поставку'}>
                      <span>
                        {marketplace === 'yandex' ? (
                          <Button
                            size="small"
                            variant="contained"
                            disabled={writeBlocked || Boolean(action) || !carriageId}
                            onClick={() => onApprove(carriage)}
                          >
                            {approveBusy ? <CircularProgress size={16} /> : 'Закрыть'}
                          </Button>
                        ) : (
                          <IconButton
                            color="primary"
                            disabled={writeBlocked || Boolean(action) || !carriageId}
                            onClick={() => onApprove(carriage)}
                          >
                            {approveBusy ? <CircularProgress size={20} /> : <ApproveIcon />}
                          </IconButton>
                        )}
                      </span>
                    </Tooltip>
                  )}
                  {marketplace === 'yandex' ? (
                    <Tooltip title={actBlockedReason || 'Скачать акт приёма-передачи'}>
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={downloadBusy ? undefined : <DownloadIcon />}
                          disabled={
                            Boolean(action)
                            || !carriageId
                            || !canDownloadAct
                          }
                          onClick={() => onDownloadAct(carriage)}
                        >
                          {downloadBusy ? <CircularProgress size={16} /> : 'Скачать акт'}
                        </Button>
                      </span>
                    </Tooltip>
                  ) : !readOnly && (
                    <Tooltip title={printBlockedReason || 'Печать этикетки / ШК'}>
                      <span>
                        <IconButton
                          color="primary"
                          disabled={
                            Boolean(action)
                            || !carriageId
                            || !canPrint
                            || Boolean(printBlockedReason)
                          }
                          onClick={() => onPrintBarcode(carriage)}
                        >
                          {printBusy ? <CircularProgress size={20} /> : <PrintIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          );
        })}
        {!carriages.length && (
          <TableRow>
            <TableCell colSpan={OZON_SUPPLY_COLUMNS.length} align="center" sx={{ py: 6 }}>
              {emptyLabel || 'Отгрузок пока нет. Они появятся после sync из ЛК Ozon или после создания отгрузки.'}
            </TableCell>
          </TableRow>
        )}
      </TableBody>

      <Dialog
        open={dialog.open}
        onClose={() => setDialog((old) => ({ ...old, open: false }))}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          Состав отгрузки {dialog.carriage?.name || dialog.carriage?.carriage_id || ''}
          {(dialog.carriage?.carriage_id || dialog.carriage?.shipment_id) && (
            <Typography variant="bodySmall" color="text.secondary" component="p">
              {dialog.carriage?.carriage_id || dialog.carriage?.shipment_id}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {dialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={32} /></Box>
          ) : dialog.error ? (
            <Alert severity="error">{dialog.error}</Alert>
          ) : dialog.items.length ? (
            <TableContainer sx={{ maxHeight: '70vh' }}>
              <Table size="small" stickyHeader aria-label={marketplace === 'yandex' ? 'Состав поставки Yandex' : 'Состав отгрузки Ozon'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Отправление / дата</TableCell>
                    <TableCell>Товар</TableCell>
                    <TableCell>Статус</TableCell>
                    {showPrintSticker && <TableCell align="right">Печать</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dialog.items.map((task) => {
                    const taskStatus = normalizedStatus(task);
                    const postingKey = task.posting_number
                      || task.yandex_order_id
                      || task.external_id
                      || task.id;
                    const printTask = { ...task, marketplace: marketplace || 'ozon' };
                    const sourceId = taskSourceId(printTask) || postingKey;
                    const canPrintRow = showPrintSticker && taskCanPrintLabel(printTask);
                    return (
                      <TableRow key={postingKey} hover>
                        <TableCell sx={{ width: 220 }}>
                          <Typography variant="bodyMedium" fontWeight={700}>
                            № {task.posting_number || task.yandex_order_id || postingKey}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(task.created_at_mp || task.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <TaskProductCell row={printTask} />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={taskStatusTooltip(task, marketplace)}>
                            <Chip
                              size="small"
                              label={taskStatus.label}
                              color={taskStatus.code === 'shipment_error' ? 'error' : undefined}
                              sx={statusChipSx(taskStatus.code)}
                            />
                          </Tooltip>
                        </TableCell>
                        {showPrintSticker && (
                          <TableCell align="right">
                            {canPrintRow && (
                              <Tooltip title="Скачать PDF этикетки">
                                <span>
                                  <IconButton
                                    color="primary"
                                    onClick={() => onPrintSticker?.(printTask)}
                                    disabled={!canPrintSticker || Boolean(printAction)}
                                  >
                                    {printAction === `sticker:${sourceId}`
                                      ? <CircularProgress size={20} />
                                      : <DownloadIcon />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              В отгрузке пока нет заказов
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog((old) => ({ ...old, open: false }))}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
